/*
 * ELIO Studio — engine + interface
 * --------------------------------------------------------------
 * Reads window.ELIO_STUDIO_CONFIG (elio-studio-config.js) for every
 * visual decision. Photos are decoded and processed on the device;
 * nothing is uploaded. One renderer draws the live preview, the
 * thumbnails, the archive and the full resolution export.
 */
(function () {
  'use strict';

  var CFG = window.ELIO_STUDIO_CONFIG;
  var root = document.querySelector('[data-elio-studio]');
  if (!CFG || !root || root.__esBooted) return;
  root.__esBooted = true;

  var OPTS = {};
  try { OPTS = JSON.parse((root.querySelector('[data-es-settings]') || {}).textContent || '{}'); } catch (e) { OPTS = {}; }
  var DEBUG = !!OPTS.debug || /[?&]studio_debug=1/.test(location.search);
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var IS_IOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  /* =============================================================
     Utilities
     ============================================================= */
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function hexRgb(h) {
    h = String(h || '#000').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h.slice(0, 6), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgba(hex, a) { var c = hexRgb(hex); return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')'; }
  function canvas(w, h) { var c = document.createElement('canvas'); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h)); return c; }
  function free(c) { if (c && c.getContext) { c.width = 0; c.height = 0; } }
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function seeded(seed) { var s = seed >>> 0 || 1; return function () { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }
  function h(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (attrs[k] == null || attrs[k] === false) continue;
      if (k === 'class') n.className = attrs[k];
      else if (k === 'text') n.textContent = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else if (k.slice(0, 2) === 'on') n.addEventListener(k.slice(2), attrs[k]);
      else if (k === 'style' && typeof attrs[k] === 'object') { for (var s in attrs[k]) n.style.setProperty(s, attrs[k][s]); }
      else n.setAttribute(k, attrs[k] === true ? '' : attrs[k]);
    }
    (kids || []).forEach(function (c) { if (c == null || c === false) return; n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }
  function debounce(fn, ms) { var t; return function () { var a = arguments, s = this; clearTimeout(t); t = setTimeout(function () { fn.apply(s, a); }, ms); }; }
  function store(k, v) { try { if (v === undefined) return JSON.parse(localStorage.getItem(k)); localStorage.setItem(k, JSON.stringify(v)); } catch (e) { return null; } }

  /* =============================================================
     Analytics: aggregate events only, never image data.
     Published as Shopify customer events (elio_studio_*) so a custom
     pixel can forward them; mirrored to dataLayer when present.
     ============================================================= */
  var sent = {};
  function track(name, props, once) {
    if (once && sent[name]) return; sent[name] = 1;
    var data = Object.assign({ studio_version: CFG.version }, props || {});
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('elio_studio_' + name, data); } catch (e) {}
    try { if (window.dataLayer && window.dataLayer.push) window.dataLayer.push(Object.assign({ event: 'elio_studio_' + name }, data)); } catch (e) {}
    if (DEBUG) console.log('[ELIO Studio]', name, data);
  }

  /* =============================================================
     Fonts
     ============================================================= */
  var fontsReady = false;
  function loadFonts() {
    if (!document.fonts || !document.fonts.load) { fontsReady = true; return Promise.resolve(); }
    var jobs = [];
    Object.keys(CFG.fonts).forEach(function (k) {
      var f = CFG.fonts[k];
      f.weights.forEach(function (w) { jobs.push(document.fonts.load(w + ' 40px ' + f.family).catch(function () {})); });
    });
    return Promise.race([Promise.all(jobs), new Promise(function (r) { setTimeout(r, 4000); })]).then(function () { fontsReady = true; });
  }

  /* =============================================================
     Image store. Each photo keeps two device-local copies:
     full (export) and proxy (live preview). Large camera files are
     stepped down on decode so mobile Safari never holds a 48MP bitmap.
     ============================================================= */
  var IMAGES = {};
  var imgSeq = 0;

  function stepDown(src, sw, sh, maxEdge) {
    var scale = Math.min(1, maxEdge / Math.max(sw, sh));
    var tw = Math.round(sw * scale), th = Math.round(sh * scale);
    var cur = src, cw = sw, ch = sh, tmp = null;
    while (cw * 0.5 > tw) {
      var nw = Math.round(cw * 0.5), nh = Math.round(ch * 0.5);
      var c = canvas(nw, nh), x = c.getContext('2d');
      x.imageSmoothingQuality = 'high'; x.drawImage(cur, 0, 0, nw, nh);
      if (tmp) free(tmp); tmp = c; cur = c; cw = nw; ch = nh;
    }
    var out = canvas(tw, th), o = out.getContext('2d');
    o.imageSmoothingQuality = 'high'; o.drawImage(cur, 0, 0, tw, th);
    if (tmp && tmp !== out) free(tmp);
    return out;
  }

  function registerImage(src, w, hgt, meta) {
    var id = 'img' + (++imgSeq);
    var full = stepDown(src, w, hgt, CFG.limits.exportEdge);
    var proxy = Math.max(full.width, full.height) > CFG.limits.previewEdge ? stepDown(full, full.width, full.height, CFG.limits.previewEdge) : full;
    IMAGES[id] = { id: id, full: full, proxy: proxy, w: full.width, h: full.height, meta: meta || {} };
    return id;
  }

  function loadImgEl(url, cors) {
    return new Promise(function (res, rej) {
      var im = new Image();
      if (cors) im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = function () { res(im); };
      im.onerror = rej;
      im.src = url;
    });
  }

  function decodeFile(file) {
    var tryBitmap = window.createImageBitmap ? createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () { return createImageBitmap(file); }) : Promise.reject();
    return tryBitmap.then(function (bmp) {
      var id = registerImage(bmp, bmp.width, bmp.height, { source: 'file' });
      if (bmp.close) bmp.close();
      return id;
    }).catch(function () {
      var url = URL.createObjectURL(file);
      return loadImgEl(url).then(function (im) {
        var id = registerImage(im, im.naturalWidth, im.naturalHeight, { source: 'file' });
        URL.revokeObjectURL(url);
        return id;
      }, function (e) { URL.revokeObjectURL(url); throw e; });
    });
  }

  function dropImage(id) {
    var im = IMAGES[id]; if (!im || im.meta.keep) return;
    if (im.proxy !== im.full) free(im.proxy);
    free(im.full); delete IMAGES[id];
    clearCache();
  }

  /* Generated photographs: used before a visitor adds their own, and
     as the archive fallback. Light studies, not illustrations. */
  function blurCanvas(c, f) {
    var s = canvas(c.width / f, c.height / f), sx = s.getContext('2d');
    sx.imageSmoothingQuality = 'high'; sx.drawImage(c, 0, 0, s.width, s.height);
    var x = c.getContext('2d'); x.clearRect(0, 0, c.width, c.height);
    x.imageSmoothingQuality = 'high'; x.drawImage(s, 0, 0, c.width, c.height); free(s);
    return c;
  }
  function generatePhoto(kind, seed) {
    var W = 1200, H = 1500, c = canvas(W, H), x = c.getContext('2d'), rnd = seeded(seed * 7919 + 11);
    var tones = [['#3b3631', '#a59d92'], ['#2a0d12', '#b0646a'], ['#121a26', '#7d8ea8'], ['#2c2218', '#c4a37c'], ['#141b17', '#7f9a88'], ['#191919', '#bdb6aa']];
    var t = tones[seed % tones.length];
    if (kind === 'blinds') {
      var g = x.createLinearGradient(0, 0, W, H); g.addColorStop(0, t[0]); g.addColorStop(1, '#0c0b0a');
      x.fillStyle = g; x.fillRect(0, 0, W, H);
      x.save(); x.translate(W * 0.15, -H * 0.05); x.rotate(-0.42 + rnd() * 0.1); x.globalCompositeOperation = 'lighter';
      for (var i = 0; i < 9; i++) { x.fillStyle = rgba(t[1], 0.3 + rnd() * 0.15); x.fillRect(-W * 0.4, i * H * 0.13, W * 1.6, H * (0.045 + rnd() * 0.02)); }
      x.restore(); blurCanvas(c, 9);
      var v = x.createRadialGradient(W * 0.55, H * 0.4, W * 0.1, W * 0.5, H * 0.5, W * 0.95); v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,.75)');
      x.fillStyle = v; x.fillRect(0, 0, W, H);
    } else if (kind === 'bokeh') {
      x.fillStyle = '#07070a'; x.fillRect(0, 0, W, H);
      var g2 = x.createLinearGradient(0, 0, 0, H); g2.addColorStop(0, rgba(t[0], 0.9)); g2.addColorStop(1, '#050505'); x.fillStyle = g2; x.fillRect(0, 0, W, H);
      x.globalCompositeOperation = 'lighter';
      var cols = ['#ffb35c', '#ff6b4a', '#ffe2b0', t[1], '#9ab8ff'];
      for (var j = 0; j < 46; j++) {
        var r = W * (0.02 + rnd() * 0.08), cx = rnd() * W, cy = H * (0.1 + rnd() * 0.7);
        var rg = x.createRadialGradient(cx, cy, r * 0.2, cx, cy, r); var col = cols[Math.floor(rnd() * cols.length)];
        rg.addColorStop(0, rgba(col, 0.32 + rnd() * 0.3)); rg.addColorStop(0.85, rgba(col, 0.22)); rg.addColorStop(1, rgba(col, 0));
        x.fillStyle = rg; x.beginPath(); x.arc(cx, cy, r, 0, 6.283); x.fill();
      }
      x.globalCompositeOperation = 'source-over'; blurCanvas(c, 3);
    } else {
      var g3 = x.createRadialGradient(W * 0.5, H * 0.36, W * 0.05, W * 0.5, H * 0.45, W * 0.9);
      g3.addColorStop(0, t[1]); g3.addColorStop(0.55, t[0]); g3.addColorStop(1, '#0a0909');
      x.fillStyle = g3; x.fillRect(0, 0, W, H);
      var fl = x.createLinearGradient(0, H * 0.7, 0, H); fl.addColorStop(0, 'rgba(0,0,0,0)'); fl.addColorStop(1, 'rgba(0,0,0,.55)');
      x.fillStyle = fl; x.fillRect(0, H * 0.7, W, H * 0.3);
    }
    var id = registerImage(c, W, H, { source: 'generated', keep: true }); free(c);
    return id;
  }
  var PLACEHOLDER = null;
  function placeholderId() { if (!PLACEHOLDER) PLACEHOLDER = generatePhoto('blinds', 0); return PLACEHOLDER; }

  /* Archive images from the theme editor (optional), else generated. */
  var ARCHIVE_IMGS = [];
  function archiveImage(i) {
    if (ARCHIVE_IMGS[i]) return ARCHIVE_IMGS[i];
    var list = OPTS.archive || [];
    var kinds = ['blinds', 'bokeh', 'backdrop', 'bokeh', 'blinds', 'backdrop'];
    var gen = generatePhoto(kinds[i % kinds.length], i + 1);
    var slot = { id: gen, ready: Promise.resolve(gen) };
    if (list.length) {
      var src = list[i % list.length];
      slot.ready = loadImgEl(src, true).then(function (im) {
        var c = canvas(im.naturalWidth, im.naturalHeight); c.getContext('2d').drawImage(im, 0, 0);
        c.getContext('2d').getImageData(0, 0, 1, 1); /* throws if tainted */
        free(c);
        var id = registerImage(im, im.naturalWidth, im.naturalHeight, { source: 'archive', keep: true });
        slot.id = id; return id;
      }).catch(function () { return gen; });
    }
    ARCHIVE_IMGS[i] = slot;
    return slot;
  }

  /* =============================================================
     Engine
     ============================================================= */
  var Engine = (function () {
    var IDENT = { exp: 0, con: 0, sat: 0, temp: 0, tint: 0, lift: 0, fade: 0, mono: 0, mix: [0.2126, 0.7152, 0.0722], split: 0, shadow: [0, 0, 0], high: [0, 0, 0], duo: 0, halation: 0, flash: 0, vignette: 0, grain: 0, halftone: 0 };
    var ARROW = [[375, 5], [468, 292], [727, 645], [395, 475], [455, 590], [455, 745], [375, 600], [297, 745], [297, 590], [357, 475], [22, 645], [282, 292]];

    var cache = new Map();
    function cacheGet(k) { var v = cache.get(k); if (v) { cache.delete(k); cache.set(k, v); } return v; }
    function cacheSet(k, v) { cache.set(k, v); while (cache.size > 36) { var f = cache.keys().next().value; free(cache.get(f)); cache.delete(f); } }
    function clearAll() { cache.forEach(function (v) { free(v); }); cache.clear(); }

    function params(doc) {
      var T = CFG.treatments[doc.treatment] || CFG.treatments.natural;
      var t = doc.intensity == null ? 1 : doc.intensity;
      var P = {};
      Object.keys(IDENT).forEach(function (k) {
        var a = IDENT[k], b = T.p[k] != null ? T.p[k] : a;
        P[k] = Array.isArray(a) ? a.map(function (v, i) { return lerp(v, b[i], t); }) : lerp(a, b, t);
      });
      if (T.p.halftone) { P.halftone = 1; P.htScale = t; }
      P.duoColors = T.p.duoColors || null;
      var A = doc.adj || {};
      P.exp += (A.exp || 0) * 0.9; P.con += (A.con || 0); P.temp += (A.temp || 0);
      P.grain = clamp(P.grain + (A.grain || 0), 0, 1.2);
      P.halation = clamp(P.halation + (A.halation || 0), 0, 1.4);
      P.vignette = clamp(P.vignette + (A.vignette || 0), 0, 1.2);
      return P;
    }

    function toneLUT(P) {
      var N = 1024, lut = new Float32Array(N), con = P.con, k = con >= 0 ? 1 + con * 1.6 : 1 / (1 - con * 1.4);
      for (var i = 0; i < N; i++) {
        var x = i / (N - 1), y = x < 0.5 ? 0.5 * Math.pow(2 * x, k) : 1 - 0.5 * Math.pow(2 - 2 * x, k);
        y = P.lift + y * (1 - P.lift - P.fade);
        lut[i] = clamp(y, 0, 1);
      }
      return lut;
    }

    function colorPass(ctx, w, hh, P, pal) {
      var img = ctx.getImageData(0, 0, w, hh), d = img.data;
      var lut = toneLUT(P), N = 1023;
      var em = Math.pow(2, P.exp), tr = 1 + P.temp * 0.14, tb = 1 - P.temp * 0.14, tg = 1 + P.tint * 0.06;
      var mono = P.mono, mx = P.mix, sat = P.sat, sp = P.split, sh = P.shadow, hi = P.high, duo = P.duo;
      var dc = P.duoColors || pal.duo, d0 = hexRgb(dc[0]), d1 = hexRgb(dc[1]);
      var d0r = d0[0] / 255, d0g = d0[1] / 255, d0b = d0[2] / 255, d1r = d1[0] / 255, d1g = d1[1] / 255, d1b = d1[2] / 255;
      for (var i = 0, n = d.length; i < n; i += 4) {
        var r = d[i] / 255 * em * tr, g = d[i + 1] / 255 * em * tg, b = d[i + 2] / 255 * em * tb, l;
        if (mono) { l = r * mx[0] + g * mx[1] + b * mx[2]; r += (l - r) * mono; g += (l - g) * mono; b += (l - b) * mono; }
        if (sat) { l = r * 0.2126 + g * 0.7152 + b * 0.0722; var s1 = 1 + sat; r = l + (r - l) * s1; g = l + (g - l) * s1; b = l + (b - l) * s1; }
        r = lut[(r < 0 ? 0 : r > 1 ? 1 : r) * N | 0]; g = lut[(g < 0 ? 0 : g > 1 ? 1 : g) * N | 0]; b = lut[(b < 0 ? 0 : b > 1 ? 1 : b) * N | 0];
        if (sp) { l = r * 0.2126 + g * 0.7152 + b * 0.0722; var ws = (1 - l) * (1 - l) * sp, wh = l * l * sp; r += sh[0] * ws + hi[0] * wh; g += sh[1] * ws + hi[1] * wh; b += sh[2] * ws + hi[2] * wh; }
        if (duo) { l = r * 0.2126 + g * 0.7152 + b * 0.0722; l = l < 0 ? 0 : l > 1 ? 1 : l; r += (d0r + (d1r - d0r) * l - r) * duo; g += (d0g + (d1g - d0g) * l - g) * duo; b += (d0b + (d1b - d0b) * l - b) * duo; }
        d[i] = r * 255; d[i + 1] = g * 255; d[i + 2] = b * 255;
      }
      ctx.putImageData(img, 0, 0);
    }

    function halation(c, amt) {
      var w = c.width, hh = c.height, f = 10;
      var s = canvas(w / f, hh / f), sx = s.getContext('2d');
      sx.drawImage(c, 0, 0, s.width, s.height);
      var im = sx.getImageData(0, 0, s.width, s.height), d = im.data;
      for (var i = 0; i < d.length; i += 4) {
        var l = Math.max(d[i], d[i + 1], d[i + 2]) / 255, v = clamp((l - 0.6) / 0.4, 0, 1); v = v * v;
        d[i] = 255 * v; d[i + 1] = 70 * v; d[i + 2] = 36 * v; d[i + 3] = 255;
      }
      sx.putImageData(im, 0, 0);
      var t = canvas(s.width / 3, s.height / 3); t.getContext('2d').drawImage(s, 0, 0, t.width, t.height);
      sx.clearRect(0, 0, s.width, s.height); sx.imageSmoothingQuality = 'high'; sx.drawImage(t, 0, 0, s.width, s.height); free(t);
      var x = c.getContext('2d'); x.save(); x.globalCompositeOperation = 'screen'; x.globalAlpha = clamp(amt, 0, 1.4) * 0.85;
      x.imageSmoothingQuality = 'high'; x.drawImage(s, 0, 0, w, hh); if (amt > 1) { x.globalAlpha = (amt - 1) * 0.8; x.drawImage(s, 0, 0, w, hh); }
      x.restore(); free(s);
    }

    function flash(c, amt) {
      var x = c.getContext('2d'), w = c.width, hh = c.height, R = Math.max(w, hh);
      var g = x.createRadialGradient(w * 0.5, hh * 0.38, 0, w * 0.5, hh * 0.38, R * 0.7);
      g.addColorStop(0, 'rgba(255,255,255,' + 0.5 * amt + ')'); g.addColorStop(1, 'rgba(255,255,255,0)');
      x.save(); x.globalCompositeOperation = 'soft-light'; x.fillStyle = g; x.fillRect(0, 0, w, hh);
      x.globalCompositeOperation = 'screen'; x.globalAlpha = 0.16 * amt; x.fillRect(0, 0, w, hh); x.restore();
    }

    function vignette(c, amt) {
      var x = c.getContext('2d'), w = c.width, hh = c.height, R = Math.sqrt(w * w + hh * hh) / 2;
      var g = x.createRadialGradient(w / 2, hh * 0.46, R * 0.32, w / 2, hh / 2, R * 1.02);
      g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,' + clamp(amt * 0.8, 0, 0.95) + ')');
      x.fillStyle = g; x.fillRect(0, 0, w, hh);
    }

    var grainTile = null;
    function grain(c, amt, unit) {
      if (!grainTile) {
        grainTile = canvas(256, 256); var gx = grainTile.getContext('2d'), im = gx.createImageData(256, 256), d = im.data, r = seeded(42);
        for (var i = 0; i < d.length; i += 4) { var v = 128 + (r() + r() + r() - 1.5) * 120; d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 255; }
        gx.putImageData(im, 0, 0);
      }
      var x = c.getContext('2d'), s = Math.max(1, unit / 1080) * 256;
      x.save(); x.globalCompositeOperation = 'overlay'; x.globalAlpha = clamp(amt, 0, 1.2) * 0.42;
      for (var yy = 0; yy < c.height; yy += s) for (var xx = 0; xx < c.width; xx += s) x.drawImage(grainTile, xx, yy, s, s);
      x.restore();
    }

    function halftone(c, P, pal, unit) {
      var w = c.width, hh = c.height, x = c.getContext('2d');
      var cell = Math.max(3, unit * lerp(0.019, 0.0085, P.htScale == null ? 0.6 : P.htScale));
      var sw = Math.max(2, Math.ceil(w / cell * 2)), shh = Math.max(2, Math.ceil(hh / cell * 2));
      var s = canvas(sw, shh), sx = s.getContext('2d'); sx.drawImage(c, 0, 0, sw, shh);
      var d = sx.getImageData(0, 0, sw, shh).data; free(s);
      var dc = pal.duo, ink = dc[0], paper = dc[1];
      x.fillStyle = paper; x.fillRect(0, 0, w, hh);
      x.fillStyle = ink; x.beginPath();
      var ang = Math.PI / 4, ca = Math.cos(ang), sa = Math.sin(ang), diag = Math.sqrt(w * w + hh * hh);
      var cx0 = w / 2, cy0 = hh / 2, n = Math.ceil(diag / cell / 2) + 1;
      for (var i = -n; i <= n; i++) for (var j = -n; j <= n; j++) {
        var u = i * cell, v = j * cell, px = cx0 + u * ca - v * sa, py = cy0 + u * sa + v * ca;
        if (px < -cell || py < -cell || px > w + cell || py > hh + cell) continue;
        var qx = clamp(Math.floor(px / w * sw), 0, sw - 1), qy = clamp(Math.floor(py / hh * shh), 0, shh - 1), o = (qy * sw + qx) * 4;
        var l = (d[o] * 0.2126 + d[o + 1] * 0.7152 + d[o + 2] * 0.0722) / 255, r = cell * 0.7 * Math.sqrt(1 - l);
        if (r < 0.35) continue;
        x.moveTo(px + r, py); x.arc(px, py, r, 0, 6.2832);
      }
      x.fill();
    }

    /* Draw one photo into one frame and develop it. */
    function frame(doc, idx, rect, pal, unit, o) {
      var slot = slotFor(doc, idx), id = photoFor(doc, idx), im = IMAGES[id] || IMAGES[placeholderId()];
      var q = o.draft ? 0.5 : 1;
      var fw = Math.max(2, Math.round(rect.w * q)), fh = Math.max(2, Math.round(rect.h * q));
      var P = params(doc);
      var key = !o.nocache && [im.id, o.src, fw, fh, JSON.stringify(slot), JSON.stringify(P), pal.duo.join(), rect.shape || ''].join('|');
      if (key) { var hit = cacheGet(key); if (hit) return hit; }
      var c = canvas(fw, fh), x = c.getContext('2d');
      var src = o.src === 'full' ? im.full : im.proxy;
      var base = Math.max(fw / im.w, fh / im.h) * (slot.s || 1);
      x.fillStyle = '#111'; x.fillRect(0, 0, fw, fh);
      x.save();
      x.translate(fw / 2 + (slot.x || 0) * fw, fh / 2 + (slot.y || 0) * fh);
      x.rotate((slot.r || 0) * Math.PI / 180);
      x.scale(base * (slot.flip ? -1 : 1), base);
      x.imageSmoothingQuality = 'high';
      x.drawImage(src, -im.w / 2, -im.h / 2, im.w, im.h);
      x.restore();
      var u = unit * q;
      colorPass(x, fw, fh, P, pal);
      if (P.halation > 0.01) halation(c, P.halation);
      if (P.flash > 0.01) flash(c, P.flash);
      if (P.halftone) halftone(c, P, pal, u);
      if (P.vignette > 0.01) vignette(c, P.vignette);
      if (P.grain > 0.01 && !P.halftone) grain(c, P.grain, u);
      if (key) cacheSet(key, c);
      return c;
    }

    function slotFor(doc, i) {
      var s = (doc.slots && doc.slots[i]) || null;
      var L = CFG.layouts[doc.layout];
      if (s && s.t) return s;
      if (L && L.multi) {
        return { x: (((i * 13) % 5) - 2) * 0.035, y: (((i * 11) % 5) - 2) * 0.03, s: 1 + ((i * 7) % 4) * 0.1, r: 0, flip: false };
      }
      return s || { x: 0, y: 0, s: 1, r: 0, flip: false };
    }
    function photoFor(doc, i) {
      var list = doc.photos || [];
      var s = doc.slots && doc.slots[i];
      if (s && s.p != null && list[s.p]) return list[s.p];
      if (!list.length) return doc.sample || placeholderId();
      return list[i % list.length];
    }

    function frames(L, W, H) {
      var out = [];
      if (L.grid) {
        var g = L.grid, cw = (g.w - g.gapX * (g.cols - 1)) / g.cols * W, ch = cw / g.ar, gy = g.gapY * W;
        var rows = clamp(Math.floor((g.maxH * H + gy) / (ch + gy)), 2, 8);
        rows = Math.min(rows, Math.max(g.rows, rows));
        var total = rows * ch + (rows - 1) * gy, y0 = g.y * H + Math.max(0, (g.maxH * H - total) / 2) * 0.3;
        for (var r = 0; r < rows; r++) for (var c = 0; c < g.cols; c++) out.push({ x: g.x * W + c * (cw + g.gapX * W), y: y0 + r * (ch + gy), w: cw, h: ch, row: r });
        return out;
      }
      (L.frames || []).forEach(function (f) {
        if (f.ar) { var w = f.w * W, hh = w / f.ar; out.push({ x: f.x * W, y: f.cy * H - hh / 2, w: w, h: hh, shape: f.shape, keyline: f.keyline }); }
        else out.push({ x: f.x * W, y: f.y * H, w: f.w * W, h: f.h * H, shape: f.shape, keyline: f.keyline });
      });
      return out;
    }

    function color(c, pal) { if (!c) return pal.ink; return c.charAt(0) === '#' ? c : (pal[c] || pal.ink); }

    function wrapLines(x, text, maxW, track) {
      var out = [];
      String(text).split('\n').forEach(function (para) {
        if (!maxW) { out.push(para); return; }
        var words = para.split(/ +/), line = '';
        words.forEach(function (wd) {
          var t = line ? line + ' ' + wd : wd;
          if (line && measure(x, t, track) > maxW) { out.push(line); line = wd; } else line = t;
        });
        out.push(line);
      });
      return out;
    }
    function measure(x, s, track) { return x.measureText(s).width + (track ? track * Math.max(0, s.length - 1) : 0); }
    function drawTracked(x, s, px, py, track) {
      if (!track) { x.fillText(s, px, py); return; }
      for (var i = 0; i < s.length; i++) { x.fillText(s[i], px, py); px += x.measureText(s[i]).width + track; }
    }

    function drawText(x, el, W, H, pal, hits) {
      var F = CFG.fonts[el.font] || CFG.fonts.mont, size = el.size * W, track = (el.track || 0) * size, lh = (el.lh || 1.15) * size;
      x.font = (el.w || 400) + ' ' + size + 'px ' + F.family;
      x.textBaseline = 'top'; x.textAlign = 'left';
      var txt = el.upper ? String(el.text).toUpperCase() : String(el.text);
      var lines = wrapLines(x, txt, el.maxW ? el.maxW * W : 0, track);
      var widths = lines.map(function (l) { return measure(x, l, track); }), maxw = Math.max.apply(null, widths.concat([1]));
      var ax = el.x * W, ty = el.y * H;
      x.save();
      x.fillStyle = color(el.color, pal); x.globalAlpha = el.opacity == null ? 1 : el.opacity;
      if (el.shadow) { x.shadowColor = 'rgba(0,0,0,' + el.shadow + ')'; x.shadowBlur = size * 0.45; x.shadowOffsetY = size * 0.04; }
      lines.forEach(function (l, i) {
        var lw = widths[i], lx = el.align === 'center' ? ax - lw / 2 : el.align === 'right' ? ax - lw : ax;
        drawTracked(x, l, lx, ty + i * lh, track);
      });
      x.restore();
      var bx = el.align === 'center' ? ax - maxw / 2 : el.align === 'right' ? ax - maxw : ax;
      if (hits) hits.push({ id: el.id, x: bx / W, y: ty / H, w: maxw / W, h: (lh * (lines.length - 1) + size * 1.05) / H });
    }

    var tintCache = {};
    function tinted(img, col) {
      var k = img.src + col; if (tintCache[k]) return tintCache[k];
      var c = canvas(img.naturalWidth, img.naturalHeight), x = c.getContext('2d');
      x.drawImage(img, 0, 0); x.globalCompositeOperation = 'source-in'; x.fillStyle = col; x.fillRect(0, 0, c.width, c.height);
      tintCache[k] = c; return c;
    }

    function drawArrow(x, cx, top, hgt, col) {
      var s = hgt / 740; x.save(); x.fillStyle = col; x.beginPath();
      ARROW.forEach(function (p, i) { var px = cx + (p[0] - 375) * s, py = top + (p[1] - 5) * s; if (i) x.lineTo(px, py); else x.moveTo(px, py); });
      x.closePath(); x.fill(); x.restore();
    }

    function drawBarcode(x, el, W, H, pal) {
      var bw = el.width * W, bh = el.height * H, bx = el.x * W, by = el.y * H, col = color(el.color, pal), r = seeded(97 + String(el.text).length * 31);
      x.save(); x.fillStyle = col;
      var unit = bw / 95, px = bx, i = 0;
      while (px < bx + bw - unit) { var wd = unit * (1 + Math.floor(r() * 3)); if (i % 2 === 0) x.fillRect(px, by, wd, bh * 0.76); px += wd; i++; }
      x.font = '400 ' + bh * 0.17 + 'px ' + CFG.fonts.mono.family; x.textBaseline = 'top'; x.textAlign = 'center';
      x.fillText(el.text || '', bx + bw / 2, by + bh * 0.8);
      x.restore();
    }

    function drawFilm(x, el, fr, W, H) {
      var list = el.frame === 'all' ? fr : [fr[el.frame || 0]].filter(Boolean);
      var rows = {};
      list.forEach(function (f) { var k = el.frame === 'all' ? f.row : 0; (rows[k] = rows[k] || []).push(f); });
      Object.keys(rows).forEach(function (k, ri) {
        var fs = rows[k], a = fs[0], z = fs[fs.length - 1], band = a.h * (el.compact ? 0.15 : 0.17), gap = a.h * 0.04;
        var x0 = el.compact ? a.x - gap * 2 : 0, x1 = el.compact ? z.x + z.w + gap * 2 : W;
        var y0 = a.y - band, y1 = a.y + a.h + band;
        x.save(); x.fillStyle = el.color; x.fillRect(x0, y0, x1 - x0, y1 - y0);
        var step = a.w / 8, holeW = step * 0.34, holeH = band * 0.26, rr = holeH * 0.22;
        x.fillStyle = 'rgba(232,226,214,.92)';
        for (var hx = x0 + step * 0.3; hx < x1 - holeW; hx += step) {
          [y0 + band * 0.4, y1 - band * 0.4 - holeH].forEach(function (hy) {
            x.beginPath(); if (x.roundRect) x.roundRect(hx, hy, holeW, holeH, rr); else x.rect(hx, hy, holeW, holeH); x.fill();
          });
        }
        x.fillStyle = el.edge; x.textBaseline = 'middle';
        x.font = '400 ' + band * 0.24 + 'px ' + CFG.fonts.mono.family;
        fs.forEach(function (f, i) {
          var n = (ri * fs.length + i + 1) * (el.compact ? 1 : 12);
          x.textAlign = 'left'; x.fillText(el.text + '  ' + n, f.x + f.w * 0.04, y0 + band * 0.14);
          x.textAlign = 'right'; x.fillText(n + 'A  ▸', f.x + f.w * 0.96, y1 - band * 0.13);
        });
        x.restore();
      });
    }

    function drawGrease(x, el, fr, W, pal) {
      var f = fr[el.frame] || fr[0]; if (!f) return;
      var pad = (el.pad || 0.02) * W, r = seeded(el.frame * 31 + 7);
      var x0 = f.x - pad, y0 = f.y - pad, x1 = f.x + f.w + pad, y1 = f.y + f.h + pad;
      var pts = [[x0, y0], [x1, y0 + pad * 0.3], [x1 - pad * 0.2, y1], [x0 + pad * 0.3, y1 - pad * 0.2], [x0 - pad * 0.1, y0 + pad * 0.6]];
      x.save(); x.strokeStyle = color(el.color, pal); x.lineCap = 'round'; x.lineJoin = 'round';
      for (var pass = 0; pass < 2; pass++) {
        x.lineWidth = W * (pass ? 0.0035 : 0.0062); x.globalAlpha = pass ? 0.55 : 0.9; x.beginPath();
        pts.forEach(function (p, i) { var j = (r() - 0.5) * pad * 0.5; if (i) x.lineTo(p[0] + j, p[1] - j); else x.moveTo(p[0] + j, p[1] + j); });
        x.stroke();
      }
      x.restore();
    }

    function drawCrops(x, el, fr, W, pal) {
      var f = fr[0]; if (!f) return; var g = W * 0.014, l = W * 0.02;
      x.save(); x.strokeStyle = color(el.color, pal); x.lineWidth = Math.max(1, W * 0.0012); x.globalAlpha = 0.85; x.beginPath();
      [[f.x, f.y, -1, -1], [f.x + f.w, f.y, 1, -1], [f.x, f.y + f.h, -1, 1], [f.x + f.w, f.y + f.h, 1, 1]].forEach(function (c) {
        x.moveTo(c[0] + c[2] * g, c[1]); x.lineTo(c[0] + c[2] * (g + l), c[1]);
        x.moveTo(c[0], c[1] + c[3] * g); x.lineTo(c[0], c[1] + c[3] * (g + l));
      });
      [f.x * 0.5, f.x + f.w + (W - f.x - f.w) * 0.5].forEach(function (cx) {
        var cy = f.y + f.h / 2, rr = W * 0.011; x.moveTo(cx + rr, cy); x.arc(cx, cy, rr, 0, 6.2832); x.moveTo(cx - rr * 1.6, cy); x.lineTo(cx + rr * 1.6, cy); x.moveTo(cx, cy - rr * 1.6); x.lineTo(cx, cy + rr * 1.6);
      });
      x.stroke(); x.restore();
    }

    function framePath(x, f) {
      x.beginPath();
      if (f.shape === 'arch') { var r = f.w / 2; x.moveTo(f.x, f.y + f.h); x.lineTo(f.x, f.y + r); x.arc(f.x + r, f.y + r, r, Math.PI, 0); x.lineTo(f.x + f.w, f.y + f.h); x.closePath(); }
      else x.rect(f.x, f.y, f.w, f.h);
    }

    /* Main render. o: {src:'proxy'|'full', draft, nocache, hits:[], wordmark:Image, credit} */
    function render(ctx, doc, W, H, o) {
      o = o || {};
      var L = CFG.layouts[doc.layout], pal = CFG.palettes[doc.palette] || CFG.palettes.black;
      var fr = frames(L, W, H), hits = o.hits;
      ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = color(L.bg, pal); ctx.fillRect(0, 0, W, H);
      doc.elements.forEach(function (el) {
        if (el.hidden) return;
        if (el.k === 'photos') {
          fr.forEach(function (f, i) {
            var c = frame(doc, i, f, pal, W, o);
            ctx.save(); framePath(ctx, f); ctx.clip(); ctx.imageSmoothingQuality = 'high'; ctx.drawImage(c, f.x, f.y, f.w, f.h); ctx.restore();
            if (f.keyline) { ctx.save(); ctx.strokeStyle = '#0b0b0b'; var kl = f.keyline * W; ctx.lineWidth = kl; ctx.strokeRect(f.x - kl / 2, f.y - kl / 2, f.w + kl, f.h + kl); ctx.restore(); }
            if (o.nocache) free(c);
            if (hits) hits.push({ frame: i, x: f.x / W, y: f.y / H, w: f.w / W, h: f.h / H });
          });
        } else if (el.k === 'text') drawText(ctx, el, W, H, pal, hits);
        else if (el.k === 'wordmark') {
          var wm = o.wordmark; if (!wm || !wm.naturalWidth) return;
          var ww = el.width * W, wh = ww * wm.naturalHeight / wm.naturalWidth, wx = el.x * W - (el.align === 'center' ? ww / 2 : el.align === 'right' ? ww : 0);
          ctx.drawImage(tinted(wm, color(el.color, pal)), wx, el.y * H, ww, wh);
          if (hits) hits.push({ id: el.id, x: wx / W, y: el.y, w: el.width, h: wh / H });
        } else if (el.k === 'mark') {
          var mh = el.size * W, mw = mh * 705 / 740; drawArrow(ctx, el.x * W, el.y * H, mh, color(el.color, pal));
          if (hits) hits.push({ id: el.id, x: (el.x * W - mw / 2) / W, y: el.y, w: mw / W, h: mh / H });
        } else if (el.k === 'rule') { ctx.fillStyle = color(el.color, pal); ctx.fillRect(el.x * W, el.y * H, el.w * W, Math.max(1, el.t * W)); }
        else if (el.k === 'barcode') { drawBarcode(ctx, el, W, H, pal); if (hits) hits.push({ id: el.id, x: el.x, y: el.y, w: el.width, h: el.height }); }
        else if (el.k === 'film') drawFilm(ctx, el, fr, W, H);
        else if (el.k === 'grease') drawGrease(ctx, el, fr, W, pal);
        else if (el.k === 'crops') drawCrops(ctx, el, fr, W, pal);
      });
      if (o.credit) {
        var cr = L.credit || { x: 0.5, y: 0.975, align: 'center', color: 'ink' };
        drawText(ctx, { id: '_credit', k: 'text', text: 'Made at ELIO Studio', font: 'mont', w: 500, size: 0.0098, x: cr.x, y: cr.y, align: cr.align, color: cr.color, track: 0.34, upper: true, opacity: 0.62 }, W, H, pal, null);
      }
      ctx.restore();
      return fr;
    }

    return { render: render, frame: frame, frames: frames, params: params, clear: clearAll, photoFor: photoFor, slotFor: slotFor, drawArrow: drawArrow };
  })();
  function clearCache() { Engine.clear(); }

  /* =============================================================
     Document model
     ============================================================= */
  function newDoc(layoutId, from) {
    var L = CFG.layouts[layoutId];
    var doc = {
      layout: layoutId, room: L.room, format: (from && from.format) || 'feed',
      palette: L.palette, treatment: L.treatment, intensity: L.intensity, adj: {},
      photos: from ? from.photos.slice() : [], sample: from ? from.sample : null,
      slots: [], elements: clone(L.elements), credit: from ? from.credit !== false : true
    };
    applyIssue(doc);
    if (from) {
      if (from.userPalette) { doc.palette = from.palette; doc.userPalette = true; }
      from.elements.forEach(function (e) {
        if (!e.edited) return;
        var t = doc.elements.filter(function (d) { return d.id === e.id && d.k === 'text'; })[0];
        if (t) { t.text = e.text; t.edited = true; }
        else if (e.k === 'text' && e.role === 'Your name') { var nm = doc.elements.filter(function (d) { return d.role === 'Your name'; })[0]; if (nm) { nm.text = e.text; nm.edited = true; } }
      });
      var nameEl = from.elements.filter(function (e) { return e.role === 'Your name' && e.edited; })[0];
      if (nameEl) doc.elements.forEach(function (e) { if (e.k === 'text' && !e.edited && /Your Name/.test(e.text)) e.text = e.text.replace(/Your Name/g, nameEl.text); });
    }
    return doc;
  }

  /* The current issue (theme editor) dates and titles the Studio covers. */
  function applyIssue(doc) {
    var I = OPTS.issue; if (!I) return;
    doc.elements.forEach(function (e) {
      if (e.k !== 'text') return;
      if (e.id === 'issue' && I.no) e.text = e.text.replace(/No\. ?\d+/, 'No. ' + I.no).replace(/Winter 2026/, I.month || 'Winter 2026');
      if (e.id === 'kicker' && doc.layout === 'issue' && I.theme) e.text = I.theme;
      if (e.id === 'issue' && doc.layout === 'monograph' && I.no) e.text = 'Monograph  No. ' + I.no;
    });
  }

  /* =============================================================
     Interface
     ============================================================= */
  var S = {
    view: 'lobby', room: null, doc: null, snap: null, past: [], future: [],
    sel: null, tab: 'photo', hits: [], frames: [], edits: 0,
    exp: { type: 'png', scale: 2 }, exportBlob: null, exportUrl: null
  };
  var WORDMARK = new Image();
  WORDMARK.crossOrigin = 'anonymous';
  if (OPTS.wordmark) WORDMARK.src = OPTS.wordmark;
  WORDMARK.onload = function () { requestRender(); refreshThumbs(); };

  var $ = function (sel, ctx) { return (ctx || root).querySelector(sel); };
  var app = $('[data-es-app]');

  /* ---------- Toast ---------- */
  var toastEl = h('div', { class: 'es-toast', role: 'status', 'aria-live': 'polite' });
  app.appendChild(toastEl);
  var toastT;
  function toast(msg, ms) { toastEl.textContent = msg; toastEl.classList.add('is-on'); clearTimeout(toastT); toastT = setTimeout(function () { toastEl.classList.remove('is-on'); }, ms || 2600); }

  /* ---------- Veil transitions ---------- */
  var veil = h('div', { class: 'es-veil', 'aria-hidden': 'true' }, [h('span', { class: 'es-veil__label' })]);
  app.appendChild(veil);
  function transition(kind, label, mid) {
    if (REDUCED) { mid(); return; }
    veil.className = 'es-veil es-veil--' + kind;
    $('.es-veil__label', veil).textContent = label || '';
    void veil.offsetWidth; veil.classList.add('is-run');
    setTimeout(mid, kind === 'strobe' ? 160 : 360);
    setTimeout(function () { veil.className = 'es-veil'; }, 1150);
  }

  /* ---------- Views ----------
     The landing lives in the page flow, inside the site header and footer.
     The editor and the archive open full screen on top of it. */
  var views = {};
  var scrollY0 = 0;
  function show(v) {
    var wasFull = root.classList.contains('is-full');
    var full = v === 'room' || v === 'archive' || v === 'set';
    if (full && !wasFull) scrollY0 = window.scrollY || 0;
    S.view = v;
    Object.keys(views).forEach(function (k) { views[k].hidden = k !== v; });
    root.setAttribute('data-view', v);
    root.classList.toggle('is-full', full);
    document.documentElement.classList.toggle('es-lock', full);
    if (!full && wasFull) window.scrollTo(0, scrollY0);
  }
  var visits = store('elio_studio_visits') || { n: 0 };

  /* ---------- Landing ---------- */
  views.lobby = $('.es-land');
  var lobbyBuilt = false;
  function buildLobby() {
    if (lobbyBuilt) { refreshLobbyArt(); return; }
    lobbyBuilt = true;
    Array.prototype.forEach.call(root.querySelectorAll('[data-room-open]'), function (b) {
      b.addEventListener('click', function () { enterRoom(b.getAttribute('data-room-open')); });
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-set-open]'), function (b) {
      b.addEventListener('click', function () { openSet(b.getAttribute('data-set-open')); });
    });
    refreshLobbyArt();
  }
  /* Each room card shows a finished example, built live by the engine
     from that room's example photo (theme editor), else a generated study. */
  var CARD_EXAMPLE = {
    studio:   { layout: 'issue',    palette: 'black',   treatment: 'flash',    gen: 'backdrop', seed: 6 },
    darkroom: { layout: 'print',    palette: 'bone',    treatment: 'silver',   gen: 'blinds',   seed: 7 },
    press:    { layout: 'poster',   palette: 'crimson', treatment: 'duotone',  gen: 'blinds',   seed: 9 },
    archive:  { layout: 'nocturne', palette: 'crimson', treatment: 'halation', gen: 'bokeh',    seed: 10, texts: { name: 'No. 07' } }
  };
  var DOOR_IMGS = {};
  function doorImage(k) {
    if (DOOR_IMGS[k]) return DOOR_IMGS[k];
    var ex = CARD_EXAMPLE[k], gen = generatePhoto(ex.gen, ex.seed);
    var slot = { id: gen, ready: Promise.resolve(gen) }, src = (OPTS.doors || {})[k];
    if (src) slot.ready = loadImgEl(src, true).then(function (im) {
      var t = canvas(2, 2); t.getContext('2d').drawImage(im, 0, 0, 2, 2); t.getContext('2d').getImageData(0, 0, 1, 1); free(t);
      slot.id = registerImage(im, im.naturalWidth, im.naturalHeight, { source: 'door', keep: true }); return slot.id;
    }).catch(function () { return gen; });
    DOOR_IMGS[k] = slot; return slot;
  }
  function refreshLobbyArt() {
    renderOffers();
    Array.prototype.forEach.call(views.lobby.querySelectorAll('.es-room-card'), function (card) {
      var k = card.getAttribute('data-room'), c = card.querySelector('canvas'), ex = CARD_EXAMPLE[k];
      var w = Math.max(240, Math.min(520, c.getBoundingClientRect().width || 320)), hh = w * 1.25;
      var slot = doorImage(k);
      var draw = function (id) {
        var d = presetDoc({ layout: ex.layout, palette: ex.palette, treatment: ex.treatment, texts: ex.texts }, id);
        var dpr = Math.min(2, window.devicePixelRatio || 1); c.width = Math.round(w * dpr); c.height = Math.round(hh * dpr);
        Engine.render(c.getContext('2d'), d, c.width, c.height, { src: 'proxy', wordmark: WORDMARK });
      };
      draw(slot.id); slot.ready.then(function (id) { draw(id); });
    });
  }
  /* The two main offers. The Set shows six developed frames; The Issue shows
     this month's cover. Both use the example photos when they are set. */
  function renderOffers() {
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    var cv = views.lobby.querySelector('[data-offer-art="issue"]');
    if (cv) {
      var slotI = doorImage('studio');
      var drawI = function (id) {
        var w = Math.max(260, Math.min(560, cv.getBoundingClientRect().width || 360)), hh = w * 1.25;
        var d = presetDoc({ layout: 'issue', palette: 'black', treatment: 'flash' }, id);
        cv.width = Math.round(w * dpr); cv.height = Math.round(hh * dpr);
        Engine.render(cv.getContext('2d'), d, cv.width, cv.height, { src: 'proxy', wordmark: WORDMARK });
      };
      drawI(slotI.id); slotI.ready.then(drawI);
    }
    var cs = views.lobby.querySelector('[data-offer-art="set"]');
    if (cs) {
      var keys = ['studio', 'darkroom', 'press', 'archive'];
      var slots = keys.map(doorImage);
      var drawS = function () {
        var w = Math.max(300, Math.min(640, cs.getBoundingClientRect().width || 420)), cols = 3, gap = w * 0.02, cw = (w - gap * (cols - 1)) / cols, ch = cw * 1.25, hh = ch * 2 + gap;
        cs.width = Math.round(w * dpr); cs.height = Math.round(hh * dpr);
        var x = cs.getContext('2d'); x.setTransform(dpr, 0, 0, dpr, 0, 0); x.clearRect(0, 0, w, hh);
        var looks = [[0, 1, 1.0, 0, 0], [1, 1.25, 1.15, 0, 0.12], [2, 1, 1.3, 0.1, -0.05], [3, 1, 1.1, -0.08, 0.05], [0, 1, 2.0, 0.12, 0.1], [1, 1, 1.0, 0, 0]];
        for (var n = 0; n < 6; n++) {
          var L = looks[n], id = slots[L[0]].id, c = canvas(Math.round(cw * dpr), Math.round(ch * dpr));
          var d = { layout: 'portrait', palette: 'black', treatment: 'clean', intensity: 0.85, adj: {}, photos: [id], slots: [{ x: L[3], y: L[4], s: L[2], r: 0, flip: n === 5, t: 1 }], elements: [{ id: 'photos', k: 'photos' }] };
          Engine.render(c.getContext('2d'), d, c.width, c.height, { src: 'proxy' });
          var px = (n % cols) * (cw + gap), py = Math.floor(n / cols) * (ch + gap);
          x.drawImage(c, px, py, cw, ch); free(c);
          x.fillStyle = 'rgba(0,0,0,.55)'; x.fillRect(px, py + ch - 22, 30, 22);
          x.fillStyle = '#c9a86a'; x.font = '400 11px ' + CFG.fonts.mono.family; x.textBaseline = 'middle'; x.fillText('0' + (n + 1), px + 7, py + ch - 11);
        }
        x.setTransform(1, 0, 0, 1, 0, 0);
      };
      drawS(); Promise.all(slots.map(function (s) { return s.ready; })).then(drawS);
    }
  }
  function presetDoc(A, imgId) {
    var d = newDoc(A.layout); d.palette = A.palette; d.treatment = A.treatment; d.intensity = A.intensity != null ? A.intensity : CFG.layouts[A.layout].intensity; d.sample = imgId;
    Object.keys(A.texts || {}).forEach(function (k) { d.elements.forEach(function (e) { if (e.id === k) e.text = A.texts[k]; }); });
    return d;
  }
  function renderPreset(A, c, w, hh) {
    var slot = archiveImage(A.img);
    var draw = function (id) {
      var d = presetDoc(A, id), dpr = Math.min(2, window.devicePixelRatio || 1);
      c.width = Math.round(w * dpr); c.height = Math.round(hh * dpr);
      Engine.render(c.getContext('2d'), d, c.width, c.height, { src: 'proxy', wordmark: WORDMARK });
    };
    draw(slot.id); slot.ready.then(function (id) { if (id !== slot.idDrawn) { slot.idDrawn = id; draw(id); } });
  }

  /* ---------- Archive ---------- */
  views.archive = h('section', { class: 'es-archive', hidden: true, 'aria-label': 'The Archive' });
  app.appendChild(views.archive);
  var archiveBuilt = false;
  function buildArchive() {
    if (archiveBuilt) return; archiveBuilt = true;
    var head = h('header', { class: 'es-bar' }, [
      h('button', { class: 'es-icon-btn', type: 'button', 'aria-label': 'Back', onclick: leaveRoom, html: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' }),
      h('div', { class: 'es-bar__title' }, [h('span', { class: 'es-bar__num', text: 'IV' }), h('span', { text: 'The Archive' })]),
      h('span', { class: 'es-bar__spacer' })
    ]);
    var intro = h('div', { class: 'es-archive__intro' }, [
      h('h1', { class: 'es-archive__title', text: 'Past issues' }),
      h('p', { text: 'Pick one. Swap in your photo.' })
    ]);
    var grid = h('div', { class: 'es-archive__grid' });
    CFG.archive.forEach(function (A, i) {
      var c = canvas(10, 10); c.setAttribute('aria-hidden', 'true');
      var L = CFG.layouts[A.layout], R = CFG.rooms[L.room];
      var card = h('button', { class: 'es-issue', type: 'button', style: { '--i': i }, onclick: function () { openPreset(A); } }, [
        h('span', { class: 'es-issue__art' }, [c]),
        h('span', { class: 'es-issue__cap' }, [
          h('span', { class: 'es-issue__no', text: 'No. ' + A.no }),
          h('span', { class: 'es-issue__name', text: A.title }),
          h('span', { class: 'es-issue__room', text: R.label })
        ])
      ]);
      grid.appendChild(card);
      requestAnimationFrame(function () { renderPreset(A, c, 300, 375); });
    });
    views.archive.appendChild(head); views.archive.appendChild(intro); views.archive.appendChild(grid);
  }
  function openPreset(A) {
    var L = CFG.layouts[A.layout];
    track('archive_issue_opened', { issue: A.no, mode: L.room });
    var slot = archiveImage(A.img);
    var d = presetDoc(A, slot.id);
    if (S.doc && S.doc.photos.length) d.photos = S.doc.photos.slice();
    startRoom(L.room, d, 'No. ' + A.no + ' loaded. Add your photo.');
  }

  /* ---------- Room (editor) ---------- */
  views.room = h('section', { class: 'es-room', hidden: true });
  app.appendChild(views.room);
  var R = {};
  function buildRoomShell() {
    R.bar = h('header', { class: 'es-bar' }, [
      h('button', { class: 'es-icon-btn', type: 'button', 'aria-label': 'Back', onclick: leaveRoom, html: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' }),
      R.title = h('div', { class: 'es-bar__title' }),
      h('div', { class: 'es-bar__tools' }, [
        R.undo = h('button', { class: 'es-icon-btn', type: 'button', 'aria-label': 'Undo', onclick: undo, html: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 7H4V2M4.5 7A8 8 0 1 1 4 13" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' }),
        R.redo = h('button', { class: 'es-icon-btn', type: 'button', 'aria-label': 'Redo', onclick: redo, html: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 7h5V2M19.5 7A8 8 0 1 0 20 13" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' }),
        h('button', { class: 'es-btn es-btn--gold es-bar__finish', type: 'button', onclick: openExport, text: 'Done' })
      ])
    ]);
    R.stage = h('div', { class: 'es-stage' });
    R.wrap = h('div', { class: 'es-canvas-wrap' });
    R.canvas = h('canvas', { class: 'es-canvas', role: 'img', 'aria-label': 'Live preview of your design' });
    R.sel = h('div', { class: 'es-sel', hidden: true });
    R.guide = h('div', { class: 'es-guide', hidden: true });
    R.empty = h('div', { class: 'es-empty' }, [
      h('p', { class: 'es-empty__k', text: 'Your photo goes here' }),
      h('p', { class: 'es-empty__t', text: 'Add a photo' }),
      h('div', { class: 'es-empty__btns' }, [
        h('button', { class: 'es-btn es-btn--gold', type: 'button', onclick: function () { pickFiles(); }, text: 'Upload' }),
        h('button', { class: 'es-btn es-btn--ghost', type: 'button', onclick: function () { openCamera(); }, text: 'Camera' })
      ]),
      h('p', { class: 'es-empty__s', text: 'It never leaves your device.' })
    ]);
    R.wrap.appendChild(R.canvas); R.wrap.appendChild(R.sel); R.wrap.appendChild(R.guide);
    R.stage.appendChild(R.wrap); R.stage.appendChild(R.empty);
    R.drop = h('div', { class: 'es-dropzone', hidden: true, text: 'Drop to develop' });
    R.stage.appendChild(R.drop);

    var tabs = [['photo', 'Photo'], ['develop', 'Develop'], ['type', 'Type'], ['design', 'Design']];
    R.tabs = h('div', { class: 'es-tabs', role: 'tablist' });
    tabs.forEach(function (t) {
      R.tabs.appendChild(h('button', { class: 'es-tab', type: 'button', role: 'tab', 'data-tab': t[0], 'aria-selected': 'false', onclick: function () { setTab(t[0]); }, text: t[1] }));
    });
    R.panel = h('div', { class: 'es-panel', role: 'tabpanel' });
    R.dock = h('aside', { class: 'es-dock' }, [R.tabs, R.panel]);
    views.room.appendChild(R.bar); views.room.appendChild(R.stage); views.room.appendChild(R.dock);

    R.file = h('input', { type: 'file', accept: 'image/*', multiple: true, class: 'es-sr', tabindex: '-1', 'aria-hidden': 'true' });
    R.file.addEventListener('change', function () { importFiles(R.file.files, 'upload'); R.file.value = ''; });
    R.cap = h('input', { type: 'file', accept: 'image/*', capture: 'user', class: 'es-sr', tabindex: '-1', 'aria-hidden': 'true' });
    R.cap.addEventListener('change', function () { importFiles(R.cap.files, 'camera'); R.cap.value = ''; });
    views.room.appendChild(R.file); views.room.appendChild(R.cap);

    bindStage();
    if (window.ResizeObserver) new ResizeObserver(function () { sizeCanvas(); }).observe(R.stage);
    else window.addEventListener('resize', sizeCanvas);
    document.addEventListener('keydown', onKey);
  }

  function enterRoom(k) {
    if (k === 'archive') {
      track('mode_selected', { mode: 'archive' });
      transition('archive', CFG.rooms.archive.enter, function () { buildArchive(); show('archive'); });
      return;
    }
    var R0 = CFG.rooms[k];
    var prev = S.doc;
    var d = (prev && prev.room === k) ? prev : newDoc(R0.layouts[0], prev);
    startRoom(k, d);
  }

  function startRoom(k, d, msg) {
    var R0 = CFG.rooms[k];
    track('mode_selected', { mode: k });
    transition(R0.transition, R0.enter, function () {
      if (!R.bar) buildRoomShell();
      S.room = k; S.doc = d; S.past = []; S.future = []; S.snap = JSON.stringify(d); S.sel = null;
      views.room.setAttribute('data-room', k);
      views.room.style.setProperty('--tint', R0.tint);
      show('room');
      setTab(d.photos.length ? 'develop' : 'photo');
      updateChrome(); sizeCanvas();
      if (msg) setTimeout(function () { toast(msg, 3600); }, 700);
    });
  }
  function leaveRoom() { flushCommit(); show('lobby'); buildLobby(); }

  function updateChrome() {
    var L = CFG.layouts[S.doc.layout], R0 = CFG.rooms[S.room];
    R.title.innerHTML = '';
    R.title.appendChild(h('span', { class: 'es-bar__num', text: R0.numeral }));
    R.title.appendChild(h('span', { text: R0.label }));
    R.title.appendChild(h('span', { class: 'es-bar__sub', text: L.label }));
    R.undo.disabled = !S.past.length; R.redo.disabled = !S.future.length;
    R.empty.hidden = !!S.doc.photos.length || !!S.doc.sample;
    views.room.classList.toggle('has-photo', !!S.doc.photos.length);
  }

  /* ---------- Canvas sizing + rendering ---------- */
  var renderQueued = false, renderDraft = false;
  function sizeCanvas() {
    if (S.view !== 'room' || !S.doc) return;
    var F = CFG.formats[S.doc.format];
    var r = R.stage.getBoundingClientRect(); if (!r.width || !r.height) return;
    var pad = r.width < 560 ? 14 : 34;
    var aw = r.width - pad * 2, ah = r.height - pad * 2, ar = F.w / F.h;
    var w = Math.min(aw, ah * ar), hh = w / ar;
    R.wrap.style.width = Math.round(w) + 'px'; R.wrap.style.height = Math.round(hh) + 'px';
    var dpr = Math.min(2, window.devicePixelRatio || 1), bw = Math.min(1500, Math.round(w * dpr));
    R.canvas.width = bw; R.canvas.height = Math.round(bw / ar);
    R.cssW = w; R.cssH = hh;
    requestRender();
  }
  function requestRender(draft) {
    renderDraft = renderDraft || !!draft;
    if (renderQueued) return; renderQueued = true;
    requestAnimationFrame(function () {
      renderQueued = false; var d = renderDraft; renderDraft = false;
      drawNow(d);
      if (d) settleRender();
    });
  }
  var settleRender = debounce(function () { requestRender(false); }, 180);
  function drawNow(draft) {
    if (S.view !== 'room' || !S.doc || !R.canvas.width) return;
    var hits = [];
    S.frames = Engine.render(R.canvas.getContext('2d'), S.doc, R.canvas.width, R.canvas.height, { src: 'proxy', draft: draft, hits: hits, wordmark: WORDMARK, credit: false });
    S.hits = hits;
    drawSelection();
  }
  function drawSelection() {
    var s = S.sel, b = null;
    if (s) b = S.hits.filter(function (x) { return s.id ? x.id === s.id : x.frame === s.frame; })[0];
    if (!b) { R.sel.hidden = true; return; }
    var pad = 6;
    R.sel.hidden = false;
    R.sel.className = 'es-sel' + (s.frame != null ? ' es-sel--frame' : '');
    R.sel.style.left = (b.x * R.cssW - (s.id ? pad : 0)) + 'px';
    R.sel.style.top = (b.y * R.cssH - (s.id ? pad : 0)) + 'px';
    R.sel.style.width = (b.w * R.cssW + (s.id ? pad * 2 : 0)) + 'px';
    R.sel.style.height = (b.h * R.cssH + (s.id ? pad * 2 : 0)) + 'px';
  }

  /* ---------- History ---------- */
  var commitT = null;
  function changed(opts) {
    opts = opts || {};
    if (opts.clearCache) clearCache();
    requestRender(opts.draft);
    if (!S.edits++) {}
    if (S.doc.photos.length && !S.firstEditSent && opts.meaningful !== false) { S.firstEditSent = true; track('first_edit', { mode: S.room, layout: S.doc.layout }, true); }
    clearTimeout(commitT);
    if (opts.now) flushCommit(); else commitT = setTimeout(flushCommit, 450);
  }
  function flushCommit() {
    clearTimeout(commitT); if (!S.doc) return;
    var j = JSON.stringify(S.doc);
    if (j !== S.snap) { S.past.push(S.snap); if (S.past.length > CFG.limits.history) S.past.shift(); S.snap = j; S.future = []; }
    if (R.undo) { R.undo.disabled = !S.past.length; R.redo.disabled = !S.future.length; }
  }
  function restore(j) {
    S.doc = JSON.parse(j); S.snap = j;
    if (S.sel && S.sel.id && !S.doc.elements.some(function (e) { return e.id === S.sel.id; })) S.sel = null;
    clearCache(); updateChrome(); sizeCanvas(); buildPanel();
  }
  function undo() { flushCommit(); if (!S.past.length) return; S.future.push(S.snap); restore(S.past.pop()); }
  function redo() { flushCommit(); if (!S.future.length) return; S.past.push(S.snap); restore(S.future.pop()); }

  /* ---------- Stage gestures ---------- */
  function bindStage() {
    var ptrs = new Map(), g = null;
    function local(e) { var r = R.wrap.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }; }
    function hitAt(p) {
      for (var i = S.hits.length - 1; i >= 0; i--) { var b = S.hits[i], m = b.id ? 0.012 : 0; if (p.x >= b.x - m && p.x <= b.x + b.w + m && p.y >= b.y - m && p.y <= b.y + b.h + m) { if (b.id) return b; } }
      for (var j = S.hits.length - 1; j >= 0; j--) { var f = S.hits[j]; if (f.frame != null && p.x >= f.x && p.x <= f.x + f.w && p.y >= f.y && p.y <= f.y + f.h) return f; }
      return null;
    }
    function elById(id) { return S.doc.elements.filter(function (e) { return e.id === id; })[0]; }
    function slot(i) {
      var d = S.doc; d.slots = d.slots || [];
      if (!d.slots[i] || !d.slots[i].t) { var base = Engine.slotFor(d, i); d.slots[i] = Object.assign({}, d.slots[i] || {}, { x: base.x, y: base.y, s: base.s, r: base.r || 0, flip: !!base.flip, t: 1 }); }
      return d.slots[i];
    }
    R.wrap.addEventListener('pointerdown', function (e) {
      if (!S.doc) return;
      R.wrap.setPointerCapture(e.pointerId);
      ptrs.set(e.pointerId, local(e));
      if (ptrs.size === 1) {
        var p = local(e), hit = hitAt(p);
        if (!hit) { S.sel = null; drawSelection(); g = null; buildPanel(); return; }
        var prevSel = S.sel && (S.sel.id || S.sel.frame);
        if (hit.id) { S.sel = { id: hit.id }; var el = elById(hit.id); g = { type: 'move', el: el, start: p, ox: el.x, oy: el.y }; }
        else { S.sel = { frame: hit.frame }; var sl = slot(hit.frame); g = { type: 'pan', slot: sl, fi: hit.frame, start: p, ox: sl.x, oy: sl.y, fw: hit.w, fh: hit.h }; }
        drawSelection();
        if ((S.sel.id || S.sel.frame) !== prevSel) syncPanelToSelection();
      } else if (ptrs.size === 2 && g) {
        var pts = Array.from(ptrs.values()), dx = pts[1].x * R.cssW - pts[0].x * R.cssW, dy = pts[1].y * R.cssH - pts[0].y * R.cssH;
        g.pinch = { d: Math.hypot(dx, dy), a: Math.atan2(dy, dx) };
        if (g.type === 'pan' || g.type === 'pinch') { g.type = 'pinch'; g.os = g.slot.s; g.or = g.slot.r; }
        else if (g.type === 'move') { g.type = 'scale'; g.osz = g.el.size || g.el.width; }
      }
    });
    R.wrap.addEventListener('pointermove', function (e) {
      if (!ptrs.has(e.pointerId) || !g) return;
      ptrs.set(e.pointerId, local(e));
      var p = local(e);
      if (ptrs.size === 1 && g.type === 'move') {
        var nx = g.ox + (p.x - g.start.x), ny = g.oy + (p.y - g.start.y), b = S.hits.filter(function (x) { return x.id === g.el.id; })[0];
        var snap = false;
        if (b) { var cx = nx + (b.x - g.el.x) + b.w / 2; if (Math.abs(cx - 0.5) < 0.012) { nx += 0.5 - cx; snap = true; } }
        g.el.x = clamp(nx, -0.2, 1.2); g.el.y = clamp(ny, -0.1, 1.05);
        R.guide.hidden = !snap; g.moved = true;
        changed({ draft: false, meaningful: true });
      } else if (ptrs.size === 1 && g.type === 'pan') {
        var sx = (p.x - g.start.x) / g.fw, sy = (p.y - g.start.y) / g.fh;
        g.slot.x = g.ox + sx; g.slot.y = g.oy + sy; clampSlot(g.fi, g.slot); g.moved = true;
        changed({ draft: true });
      } else if (ptrs.size === 2 && g.pinch) {
        var pts = Array.from(ptrs.values()), dx = pts[1].x * R.cssW - pts[0].x * R.cssW, dy = pts[1].y * R.cssH - pts[0].y * R.cssH;
        var k = Math.hypot(dx, dy) / (g.pinch.d || 1), da = (Math.atan2(dy, dx) - g.pinch.a) * 180 / Math.PI;
        if (g.type === 'pinch') { g.slot.s = clamp(g.os * k, 1, 6); g.slot.r = clamp(g.or + da, -180, 180); clampSlot(g.fi, g.slot); changed({ draft: true }); }
        else if (g.type === 'scale') { if (g.el.size != null) g.el.size = clamp(g.osz * k, 0.006, 0.3); else if (g.el.width != null) g.el.width = clamp(g.osz * k, 0.04, 1.2); changed({}); }
      }
    });
    function end(e) {
      ptrs.delete(e.pointerId);
      if (ptrs.size === 0) { R.guide.hidden = true; if (g && g.moved) { requestRender(false); flushCommit(); syncSliders(); } g = null; }
    }
    R.wrap.addEventListener('pointerup', end);
    R.wrap.addEventListener('pointercancel', end);
    R.wrap.addEventListener('dblclick', function (e) {
      var hit = hitAt(local(e)); if (hit && hit.id) { var el = elById(hit.id); if (el && el.k === 'text') { S.sel = { id: el.id }; setTab('type'); setTimeout(function () { var ta = R.panel.querySelector('textarea'); if (ta) { ta.focus(); ta.select(); } }, 60); } }
    });
    R.wrap.addEventListener('wheel', function (e) {
      if (!S.doc) return; var hit = hitAt(local(e)); if (!hit || hit.frame == null) return;
      e.preventDefault(); var sl = slot(hit.frame);
      sl.s = clamp(sl.s * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0018)), 1, 6); clampSlot(hit.frame, sl);
      S.sel = { frame: hit.frame }; drawSelection(); changed({ draft: true }); syncSliders();
    }, { passive: false });

    /* drag and drop files */
    ['dragenter', 'dragover'].forEach(function (t) { R.stage.addEventListener(t, function (e) { if (e.dataTransfer && Array.prototype.indexOf.call(e.dataTransfer.types || [], 'Files') > -1) { e.preventDefault(); R.drop.hidden = false; } }); });
    ['dragleave', 'drop'].forEach(function (t) { R.stage.addEventListener(t, function (e) { if (t === 'drop') { e.preventDefault(); if (e.dataTransfer.files.length) importFiles(e.dataTransfer.files, 'upload'); } R.drop.hidden = true; }); });
    S._slot = slot; S._elById = elById;
  }

  function onKey(e) {
    if (S.view !== 'room') return;
    var tag = (e.target.tagName || '').toLowerCase(); if (tag === 'textarea' || tag === 'input') return;
    var mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
    if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); return; }
    if (S.sel && S.sel.id && /^Arrow/.test(e.key)) {
      var el = S._elById(S.sel.id); if (!el) return; e.preventDefault(); var st = e.shiftKey ? 0.02 : 0.004;
      if (e.key === 'ArrowLeft') el.x -= st; if (e.key === 'ArrowRight') el.x += st; if (e.key === 'ArrowUp') el.y -= st; if (e.key === 'ArrowDown') el.y += st;
      changed({});
    }
    if (e.key === 'Escape') { S.sel = null; drawSelection(); }
  }

  /* ---------- Import ---------- */
  function pickFiles() { R.file.click(); }
  function importFiles(files, source) {
    files = Array.prototype.slice.call(files || []).filter(function (f) { return /^image\//.test(f.type) || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name); });
    if (!files.length) return;
    var room = CFG.limits.maxPhotos - S.doc.photos.length;
    if (room <= 0) { toast('Twelve photos max.'); return; }
    files = files.slice(0, room);
    views.room.classList.add('is-busy'); toast('Adding…', 8000);
    var done = 0, ok = 0, firstIdx = S.doc.photos.length;
    var next = function () {
      if (!files.length) {
        views.room.classList.remove('is-busy');
        if (ok) {
          toast(ok > 1 ? ok + ' photos added.' : 'Photo added.');
          track('photo_imported', { source: source, count: ok, mode: S.room });
          var sel = S.sel && S.sel.frame != null ? S.sel.frame : 0;
          if (S.doc.photos.length > 1 && S.sel && S.sel.frame != null && !CFG.layouts[S.doc.layout].multi) { S._slot(sel).p = firstIdx; }
          S.doc.sample = null; updateChrome(); changed({ clearCache: true, now: true, meaningful: false }); buildPanel(); refreshThumbs();
        } else toast('That file could not be opened here. Try a JPG or PNG.', 4000);
        return;
      }
      var f = files.shift();
      decodeFile(f).then(function (id) { S.doc.photos.push(id); ok++; }).catch(function () {}).then(function () { done++; setTimeout(next, 30); });
    };
    next();
  }

  /* ---------- Camera ---------- */
  var cam = null;
  function openCamera(o) {
    o = o || {};
    var fallback = o.onFallback || function () { R.cap.click(); };
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) || !window.isSecureContext) { fallback(); return; }
    if (!cam) {
      cam = {};
      cam.video = h('video', { class: 'es-cam__video', playsinline: true, muted: true, autoplay: true });
      cam.count = h('div', { class: 'es-cam__count', 'aria-live': 'assertive' });
      cam.timerBtn = h('button', { class: 'es-chip', type: 'button', onclick: function () { cam.timer = cam.timer === 0 ? 3 : cam.timer === 3 ? 10 : 0; cam.timerBtn.textContent = cam.timer ? 'Timer ' + cam.timer + 's' : 'Timer off'; }, text: 'Timer off' });
      cam.timer = 0; cam.facing = 'user';
      cam.hint = h('p', { class: 'es-cam__hint' });
      cam.el = h('div', { class: 'es-modal es-cam', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Camera', hidden: true }, [
        h('div', { class: 'es-cam__view' }, [cam.video, cam.count, h('div', { class: 'es-cam__guides', 'aria-hidden': 'true' }), cam.hint]),
        h('div', { class: 'es-cam__bar' }, [
          h('button', { class: 'es-chip', type: 'button', onclick: closeCamera, text: 'Close' }),
          h('button', { class: 'es-shutter', type: 'button', 'aria-label': 'Take photo', onclick: shoot }),
          h('div', { class: 'es-cam__side' }, [cam.timerBtn, h('button', { class: 'es-chip', type: 'button', onclick: function () { cam.facing = cam.facing === 'user' ? 'environment' : 'user'; startStream(); }, text: 'Flip' })])
        ])
      ]);
      app.appendChild(cam.el);
    }
    cam.onShot = o.onShot || null; cam.fallback = fallback;
    cam.hint.textContent = o.hint || ''; cam.hint.hidden = !o.hint;
    if (o.timer != null) { cam.timer = o.timer; cam.timerBtn.textContent = cam.timer ? 'Timer ' + cam.timer + 's' : 'Timer off'; }
    if (o.facing) cam.facing = o.facing;
    cam.el.hidden = false; startStream();
  }
  function startStream() {
    stopStream();
    navigator.mediaDevices.getUserMedia({ video: { facingMode: cam.facing, width: { ideal: 2560 }, height: { ideal: 1920 } }, audio: false }).then(function (st) {
      cam.stream = st; cam.video.srcObject = st; cam.video.classList.toggle('is-mirror', cam.facing === 'user'); cam.video.play().catch(function () {});
    }).catch(function () { closeCamera(); (cam.fallback || function () { R.cap.click(); })(); });
  }
  function stopStream() { if (cam && cam.stream) { cam.stream.getTracks().forEach(function (t) { t.stop(); }); cam.stream = null; } }
  function closeCamera() { stopStream(); if (cam) cam.el.hidden = true; }
  function shoot() {
    var n = cam.timer;
    var fire = function () {
      var v = cam.video; if (!v.videoWidth) return;
      var c = canvas(v.videoWidth, v.videoHeight), x = c.getContext('2d');
      if (cam.facing === 'user') { x.translate(c.width, 0); x.scale(-1, 1); }
      x.drawImage(v, 0, 0);
      cam.el.classList.add('is-flash'); setTimeout(function () { cam.el.classList.remove('is-flash'); }, 380);
      var id = registerImage(c, c.width, c.height, { source: 'camera' }); free(c);
      if (cam.onShot) { closeCamera(); cam.onShot(id); return; }
      S.doc.photos.push(id); S.doc.sample = null;
      track('photo_imported', { source: 'camera', count: 1, mode: S.room });
      closeCamera(); updateChrome(); changed({ clearCache: true, now: true, meaningful: false }); buildPanel(); refreshThumbs();
      toast('Developed.');
    };
    if (!n) return fire();
    var t = n; cam.count.textContent = t;
    var iv = setInterval(function () { t--; if (t <= 0) { clearInterval(iv); cam.count.textContent = ''; fire(); } else cam.count.textContent = t; }, 1000);
  }

  /* ---------- Panels ---------- */
  function setTab(t) {
    S.tab = t;
    Array.prototype.forEach.call(R.tabs.children, function (b) { var on = b.getAttribute('data-tab') === t; b.setAttribute('aria-selected', on ? 'true' : 'false'); b.classList.toggle('is-on', on); });
    buildPanel();
  }
  function syncPanelToSelection() {
    if (S.sel && S.sel.id) { var el = S._elById(S.sel.id); if (el && S.tab !== 'type') { setTab('type'); return; } }
    if (S.sel && S.sel.frame != null && S.tab === 'type') { setTab('photo'); return; }
    buildPanel();
  }
  function section(title, kids, cls) { return h('div', { class: 'es-group ' + (cls || '') }, [title ? h('p', { class: 'es-group__t', text: title }) : null].concat(kids)); }
  function slider(label, val, min, max, step, fmt, onInput) {
    var out = h('output', { class: 'es-range__v', text: fmt(val) });
    var inp = h('input', { type: 'range', min: min, max: max, step: step, value: val, 'aria-label': label });
    inp.addEventListener('input', function () { var v = parseFloat(inp.value); out.textContent = fmt(v); onInput(v, true); });
    inp.addEventListener('change', function () { onInput(parseFloat(inp.value), false); flushCommit(); });
    inp.addEventListener('dblclick', function () { var d = inp.getAttribute('data-default'); if (d != null) { inp.value = d; inp.dispatchEvent(new Event('input')); inp.dispatchEvent(new Event('change')); } });
    var row = h('label', { class: 'es-range' }, [h('span', { class: 'es-range__l', text: label }), inp, out]);
    row._inp = inp; row._out = out; row._fmt = fmt;
    return row;
  }
  var pct = function (v) { return Math.round(v * 100) + ''; };
  var sgn = function (v) { var n = Math.round(v * 100); return (n > 0 ? '+' : '') + n; };

  function buildPanel() {
    if (!R.panel || !S.doc) return;
    R.panel.innerHTML = '';
    R.panel.scrollTop = 0;
    ({ photo: panelPhoto, develop: panelDevelop, type: panelType, design: panelDesign })[S.tab]();
  }

  var sliderRefs = {};
  function syncSliders() {
    if (S.tab === 'photo' && sliderRefs.zoom) {
      var i = currentFrame(), sl = Engine.slotFor(S.doc, i);
      sliderRefs.zoom._inp.value = sl.s; sliderRefs.zoom._out.textContent = sliderRefs.zoom._fmt(sl.s);
      sliderRefs.rot._inp.value = sl.r || 0; sliderRefs.rot._out.textContent = sliderRefs.rot._fmt(sl.r || 0);
    }
    if (S.tab === 'type' && sliderRefs.size && S.sel && S.sel.id) {
      var el = S._elById(S.sel.id); if (el) { var v = el.size != null ? el.size : el.width; sliderRefs.size._inp.value = v; sliderRefs.size._out.textContent = sliderRefs.size._fmt(v); }
    }
  }
  function clampSlot(i, sl) {
    var f = S.frames && S.frames[i]; if (!f || !sl) return;
    var im = IMAGES[Engine.photoFor(S.doc, i)]; if (!im) return;
    var base = Math.max(f.w / im.w, f.h / im.h) * (sl.s || 1);
    var r = Math.abs(sl.r || 0) % 180, rot = Math.min(r, 180 - r) * Math.PI / 180;
    var cw = im.w * base, ch = im.h * base;
    if (rot > 0.004) { var k = Math.cos(rot); cw = cw * k - f.h * Math.sin(rot); ch = ch * k - f.w * Math.sin(rot); }
    var mx = Math.max(0, (cw - f.w) / 2 / f.w), my = Math.max(0, (ch - f.h) / 2 / f.h);
    sl.x = clamp(sl.x || 0, -mx, mx); sl.y = clamp(sl.y || 0, -my, my);
  }
  function currentFrame() { return S.sel && S.sel.frame != null ? S.sel.frame : 0; }

  function photoThumb(id) {
    var im = IMAGES[id], c = canvas(120, 150);
    if (im) { var x = c.getContext('2d'), s = Math.max(120 / im.w, 150 / im.h); x.drawImage(im.proxy, (120 - im.w * s) / 2, (150 - im.h * s) / 2, im.w * s, im.h * s); }
    return c;
  }

  function panelPhoto() {
    sliderRefs = {};
    var d = S.doc, L = CFG.layouts[d.layout], fi = currentFrame();
    R.panel.appendChild(section(null, [
      h('div', { class: 'es-row' }, [
        h('button', { class: 'es-btn es-btn--gold', type: 'button', onclick: pickFiles, text: d.photos.length ? 'Add photos' : 'Upload' }),
        h('button', { class: 'es-btn es-btn--ghost', type: 'button', onclick: openCamera, text: 'Camera' })
      ])
    ]));
    if (d.photos.length) {
      var strip = h('div', { class: 'es-strip', role: 'list' });
      d.photos.forEach(function (id, i) {
        var active = Engine.photoFor(d, fi) === id;
        var t = h('div', { class: 'es-strip__item' + (active ? ' is-on' : ''), role: 'listitem' }, [
          h('button', { class: 'es-strip__pick', type: 'button', 'aria-label': 'Place photo ' + (i + 1) + ' in ' + (L.multi ? 'frame ' + (fi + 1) : 'the frame'), onclick: function () { S._slot(fi).p = i; changed({ now: true }); buildPanel(); } }, [photoThumb(id)]),
          h('button', { class: 'es-strip__x', type: 'button', 'aria-label': 'Remove photo ' + (i + 1), onclick: function () { removePhoto(i); }, text: '×' })
        ]);
        strip.appendChild(t);
      });
      R.panel.appendChild(section('Your photos', [strip, L.multi ? h('p', { class: 'es-note', text: 'Tap a frame, then a photo.' }) : (d.photos.length > 1 ? h('p', { class: 'es-note', text: 'Tap a photo to use it.' }) : null)]));
    }
    var sl = Engine.slotFor(d, fi);
    var setSlot = function (k, v, live) { var s = S._slot(fi); s[k] = v; clampSlot(fi, s); changed({ draft: live }); };
    sliderRefs.zoom = slider('Zoom', sl.s, 1, 5, 0.01, function (v) { return v.toFixed(2) + '×'; }, function (v, live) { setSlot('s', v, live); });
    sliderRefs.zoom._inp.setAttribute('data-default', 1);
    sliderRefs.rot = slider('Rotate', sl.r || 0, -45, 45, 0.5, function (v) { return Math.round(v) + '°'; }, function (v, live) { setSlot('r', v, live); });
    sliderRefs.rot._inp.setAttribute('data-default', 0);
    R.panel.appendChild(section(L.multi ? 'Frame ' + (fi + 1) : 'Framing', [
      h('p', { class: 'es-note', text: 'Drag to move. Pinch or scroll to zoom.' }),
      sliderRefs.zoom, sliderRefs.rot,
      h('div', { class: 'es-row es-row--wrap' }, [
        h('button', { class: 'es-chip', type: 'button', onclick: function () { var s = S._slot(fi); s.r = ((Math.round((s.r || 0) / 90) * 90 + 90 + 180) % 360) - 180; changed({ now: true }); buildPanel(); }, text: 'Turn 90°' }),
        h('button', { class: 'es-chip', type: 'button', onclick: function () { var s = S._slot(fi); s.flip = !s.flip; changed({ now: true }); }, text: 'Mirror' }),
        h('button', { class: 'es-chip', type: 'button', onclick: function () { var s = S._slot(fi); s.x = 0; s.y = 0; s.s = 1; s.r = 0; s.flip = false; changed({ now: true }); buildPanel(); }, text: 'Reset framing' }),
        L.multi ? h('button', { class: 'es-chip', type: 'button', onclick: function () { var g = d.elements.filter(function (e) { return e.k === 'grease'; })[0]; if (g) { g.frame = fi; g.hidden = false; changed({ now: true }); toast('Marked frame ' + (fi + 1) + '.'); } }, text: 'Mark this frame' }) : null
      ])
    ]));
  }
  function removePhoto(i) {
    var d = S.doc, id = d.photos[i];
    d.photos.splice(i, 1);
    (d.slots || []).forEach(function (s) { if (!s || s.p == null) return; if (s.p === i) s.p = null; else if (s.p > i) s.p--; });
    if (!S.past.some(function (j) { return j.indexOf('"' + id + '"') > -1; })) dropImage(id);
    updateChrome(); changed({ clearCache: true, now: true }); buildPanel();
  }

  var thumbJobs = [];
  function refreshThumbs() { if (S.view === 'room' && (S.tab === 'develop' || S.tab === 'design')) buildPanel(); }
  function runThumbJobs() {
    var job = thumbJobs.shift(); if (!job) return;
    try { job(); } catch (e) {}
    if (thumbJobs.length) { if (window.requestIdleCallback) requestIdleCallback(runThumbJobs, { timeout: 60 }); else setTimeout(runThumbJobs, 16); }
  }

  function panelDevelop() {
    var d = S.doc, room = CFG.rooms[S.room];
    var list = room.treatments.concat(Object.keys(CFG.treatments).filter(function (k) { return room.treatments.indexOf(k) < 0; }));
    var grid = h('div', { class: 'es-swatches', role: 'radiogroup', 'aria-label': 'Treatment' });
    thumbJobs = [];
    var fi = currentFrame(), id = Engine.photoFor(d, fi), pal = CFG.palettes[d.palette];
    list.forEach(function (k) {
      var T = CFG.treatments[k], c = canvas(84, 105);
      var b = h('button', { class: 'es-swatch' + (d.treatment === k ? ' is-on' : ''), type: 'button', role: 'radio', 'aria-checked': d.treatment === k ? 'true' : 'false', onclick: function () {
        if (d.treatment === k) return; d.treatment = k; d.intensity = (CFG.layouts[d.layout].treatment === k ? CFG.layouts[d.layout].intensity : 0.85);
        changed({ now: true }); buildPanel();
      } }, [c, h('span', { text: T.label })]);
      grid.appendChild(b);
      thumbJobs.push(function () {
        var td = { layout: d.layout, treatment: k, intensity: CFG.layouts[d.layout].treatment === k ? CFG.layouts[d.layout].intensity : 0.85, adj: {}, photos: d.photos, sample: d.sample, slots: d.slots, palette: d.palette };
        var f = Engine.frame(td, fi, { x: 0, y: 0, w: 84 * 2, h: 105 * 2 }, pal, 1080 * 0.16, { src: 'proxy' });
        c.width = 168; c.height = 210; c.getContext('2d').drawImage(f, 0, 0);
      });
    });
    runThumbJobs();
    R.panel.appendChild(section('Treatment', [grid]));
    var inten = slider('Intensity', d.intensity, 0, 1, 0.01, pct, function (v, live) { d.intensity = v; changed({ draft: live }); });
    inten._inp.setAttribute('data-default', CFG.layouts[d.layout].intensity);
    R.panel.appendChild(section(null, [inten]));
    var adj = CFG.adjustments.map(function (a) {
      var row = slider(a.label, (d.adj || {})[a.id] || 0, a.min, a.max, 0.01, a.min < 0 ? sgn : pct, function (v, live) { d.adj = d.adj || {}; d.adj[a.id] = v; changed({ draft: live }); });
      row._inp.setAttribute('data-default', 0); return row;
    });
    R.panel.appendChild(section('Adjust', adj.concat([h('button', { class: 'es-chip', type: 'button', onclick: function () { d.adj = {}; changed({ now: true }); buildPanel(); }, text: 'Reset adjustments' })])));
  }

  function roleLabel(el) { return el.role || (el.k === 'text' ? 'Text' : el.k); }
  function panelType() {
    sliderRefs = {};
    var d = S.doc;
    var editable = d.elements.filter(function (e) { return e.k === 'text' || e.k === 'wordmark' || e.k === 'mark' || e.k === 'barcode'; });
    var chips = h('div', { class: 'es-row es-row--wrap es-layers' });
    editable.forEach(function (el) {
      var on = S.sel && S.sel.id === el.id;
      chips.appendChild(h('button', { class: 'es-chip' + (on ? ' is-on' : '') + (el.hidden ? ' is-off' : ''), type: 'button', 'aria-pressed': on ? 'true' : 'false', onclick: function () { S.sel = { id: el.id }; drawSelection(); buildPanel(); }, text: roleLabel(el) }));
    });
    chips.appendChild(h('button', { class: 'es-chip es-chip--add', type: 'button', onclick: addText, text: '+ Add text' }));
    R.panel.appendChild(section('Elements', [chips, !S.sel || !S.sel.id ? h('p', { class: 'es-note', text: 'Tap any text to edit it. Drag to move it.' }) : null]));
    var el = S.sel && S.sel.id && S._elById(S.sel.id);
    if (!el) return;
    var pal = CFG.palettes[d.palette];
    var kids = [];
    if (el.k === 'text') {
      var ta = h('textarea', { class: 'es-textarea', rows: Math.min(6, String(el.text).split('\n').length + 1), 'aria-label': roleLabel(el) + ' text', spellcheck: 'false' });
      ta.value = el.text;
      ta.addEventListener('input', function () { el.text = ta.value.replace(/—|–/g, ' '); el.edited = true; changed({}); });
      kids.push(ta);
      if (el.suggest && CFG.suggest[el.suggest]) {
        kids.push(h('button', { class: 'es-chip es-chip--suggest', type: 'button', onclick: function () {
          var list = CFG.suggest[el.suggest]; el._si = ((el._si || 0) + 1) % list.length; var t = list[el._si];
          var nm = d.elements.filter(function (e) { return e.role === 'Your name' && e.edited; })[0]; if (nm) t = t.replace(/Your Name/g, nm.text);
          if (el.maxW == null && String(el.text).indexOf('\n') > -1) t = breakLine(t);
          el.text = t; el.edited = true; ta.value = t; changed({ now: true });
        }, text: 'Suggest a line' }));
      }
      var fonts = h('div', { class: 'es-fonts', role: 'radiogroup', 'aria-label': 'Typeface' });
      Object.keys(CFG.fonts).forEach(function (k) {
        var F = CFG.fonts[k];
        fonts.appendChild(h('button', { class: 'es-font' + (el.font === k ? ' is-on' : ''), type: 'button', role: 'radio', 'aria-checked': el.font === k ? 'true' : 'false', style: { 'font-family': F.family, 'font-weight': F.weights.indexOf(el.w) > -1 ? el.w : F.weights[0] }, onclick: function () {
          el.font = k; if (F.weights.indexOf(el.w) < 0) el.w = F.weights[Math.min(1, F.weights.length - 1)]; changed({ now: true }); buildPanel();
        }, text: 'Aa' }, []));
        fonts.lastChild.appendChild(h('span', { text: F.label }));
      });
      kids.push(fonts);
      var F = CFG.fonts[el.font];
      if (F.weights.length > 1) {
        var wr = h('div', { class: 'es-row es-row--wrap' });
        F.weights.forEach(function (w) { wr.appendChild(h('button', { class: 'es-chip' + (el.w === w ? ' is-on' : ''), type: 'button', onclick: function () { el.w = w; changed({ now: true }); buildPanel(); }, text: ({ 200: 'Hairline', 300: 'Light', 400: 'Regular', 500: 'Medium', 600: 'Semi' })[w] || w })); });
        kids.push(wr);
      }
      sliderRefs.size = slider('Size', el.size, 0.008, 0.2, 0.0005, function (v) { return Math.round(v * 1000) / 10 + ''; }, function (v) { el.size = v; changed({}); });
      kids.push(sliderRefs.size);
      kids.push(slider('Tracking', el.track || 0, -0.05, 0.6, 0.005, function (v) { return Math.round(v * 100) + ''; }, function (v) { el.track = v; changed({}); }));
      kids.push(slider('Leading', el.lh || 1.15, 0.8, 2, 0.01, function (v) { return v.toFixed(2); }, function (v) { el.lh = v; changed({}); }));
      var al = h('div', { class: 'es-row' });
      ['left', 'center', 'right'].forEach(function (a) { al.appendChild(h('button', { class: 'es-chip' + (el.align === a ? ' is-on' : ''), type: 'button', onclick: function () { var b = S.hits.filter(function (x) { return x.id === el.id; })[0]; if (b) el.x = a === 'left' ? b.x : a === 'center' ? b.x + b.w / 2 : b.x + b.w; el.align = a; changed({ now: true }); buildPanel(); }, text: a.charAt(0).toUpperCase() + a.slice(1) })); });
      al.appendChild(h('button', { class: 'es-chip' + (el.upper ? ' is-on' : ''), type: 'button', onclick: function () { el.upper = !el.upper; changed({ now: true }); buildPanel(); }, text: 'Caps' }));
      kids.push(al);
    } else {
      var key = el.size != null ? 'size' : 'width';
      sliderRefs.size = slider('Size', el[key], key === 'size' ? 0.01 : 0.04, key === 'size' ? 0.3 : 1.1, 0.001, function (v) { return Math.round(v * 100) + ''; }, function (v) { el[key] = v; changed({}); });
      kids.push(sliderRefs.size);
    }
    var sw = h('div', { class: 'es-colors', role: 'radiogroup', 'aria-label': 'Color' });
    var roles = [['paper', 'Paper'], ['ink', 'Ink'], ['accent', 'Accent'], ['bg', 'Field']];
    roles.forEach(function (r) { sw.appendChild(h('button', { class: 'es-color' + (el.color === r[0] ? ' is-on' : ''), type: 'button', role: 'radio', 'aria-checked': el.color === r[0] ? 'true' : 'false', 'aria-label': r[1], title: r[1], style: { '--c': pal[r[0]] }, onclick: function () { el.color = r[0]; changed({ now: true }); buildPanel(); } })); });
    CFG.swatches.forEach(function (c) { sw.appendChild(h('button', { class: 'es-color' + (el.color === c ? ' is-on' : ''), type: 'button', role: 'radio', 'aria-checked': el.color === c ? 'true' : 'false', 'aria-label': c, style: { '--c': c }, onclick: function () { el.color = c; changed({ now: true }); buildPanel(); } })); });
    kids.push(sw);
    var idx = d.elements.indexOf(el);
    kids.push(h('div', { class: 'es-row es-row--wrap' }, [
      h('button', { class: 'es-chip', type: 'button', disabled: idx >= d.elements.length - 1, onclick: function () { d.elements.splice(idx, 1); d.elements.splice(idx + 1, 0, el); changed({ now: true }); buildPanel(); }, text: 'Bring forward' }),
      h('button', { class: 'es-chip', type: 'button', disabled: idx <= 0, onclick: function () { d.elements.splice(idx, 1); d.elements.splice(idx - 1, 0, el); changed({ now: true }); buildPanel(); }, text: 'Send back' }),
      h('button', { class: 'es-chip', type: 'button', onclick: function () { el.hidden = !el.hidden; changed({ now: true }); buildPanel(); }, text: el.hidden ? 'Show' : 'Hide' }),
      el.added ? h('button', { class: 'es-chip', type: 'button', onclick: function () { d.elements.splice(idx, 1); S.sel = null; changed({ now: true }); buildPanel(); }, text: 'Delete' }) : null
    ]));
    R.panel.appendChild(section(roleLabel(el), kids));
  }
  function breakLine(t) { var w = t.split(' '); if (w.length < 3) return t; var m = Math.ceil(w.length / 2); return w.slice(0, m).join(' ') + '\n' + w.slice(m).join(' '); }
  function addText() {
    var d = S.doc, L = CFG.layouts[d.layout], dark = L.bg === 'black' || (L.bg === 'bg' && d.palette !== 'tan' && d.palette !== 'bone') || (L.frames && L.frames[0] && L.frames[0].w === 1 && L.frames[0].h === 1);
    var el = { id: 't' + Date.now().toString(36), k: 'text', role: 'Text', text: 'Your line here', font: 'mont', w: 400, size: 0.03, x: 0.5, y: 0.5, align: 'center', color: dark ? 'paper' : 'ink', track: 0.12, upper: true, added: true, edited: true };
    d.elements.push(el); S.sel = { id: el.id }; changed({ now: true }); buildPanel();
    setTimeout(function () { var ta = R.panel.querySelector('textarea'); if (ta) { ta.focus(); ta.select(); } }, 50);
  }

  function panelDesign() {
    var d = S.doc, room = CFG.rooms[S.room];
    var lay = h('div', { class: 'es-layouts' });
    thumbJobs = [];
    room.layouts.forEach(function (k) {
      var L = CFG.layouts[k], c = canvas(10, 10);
      lay.appendChild(h('button', { class: 'es-layout' + (d.layout === k ? ' is-on' : ''), type: 'button', 'aria-pressed': d.layout === k ? 'true' : 'false', onclick: function () {
        if (d.layout === k) return;
        var nd = newDoc(k, d); nd.format = d.format; if (!d.userPalette) nd.palette = L.palette;
        S.doc = nd; S.sel = null; changed({ now: true, clearCache: true }); updateChrome(); sizeCanvas(); buildPanel();
      } }, [c, h('span', { class: 'es-layout__n', text: L.label }), h('span', { class: 'es-layout__d', text: L.note })]));
      thumbJobs.push(function () {
        var td = d.layout === k ? d : newDoc(k, d); if (d.layout !== k && !d.userPalette) td.palette = L.palette;
        var F = CFG.formats[d.format]; c.width = 180; c.height = Math.round(180 * F.h / F.w);
        Engine.render(c.getContext('2d'), td, c.width, c.height, { src: 'proxy', wordmark: WORDMARK });
      });
    });
    R.panel.appendChild(section('Layout', [lay]));
    var fm = h('div', { class: 'es-row' });
    Object.keys(CFG.formats).forEach(function (k) {
      var F = CFG.formats[k];
      fm.appendChild(h('button', { class: 'es-chip es-chip--fmt' + (d.format === k ? ' is-on' : ''), type: 'button', onclick: function () { d.format = k; changed({ now: true, clearCache: true }); sizeCanvas(); buildPanel(); } }, [h('i', { class: 'es-fmt-ico', style: { '--ar': F.w / F.h } }), F.label + ' ' + F.ratio]));
    });
    R.panel.appendChild(section('Format', [fm]));
    var pals = h('div', { class: 'es-palettes', role: 'radiogroup', 'aria-label': 'Palette' });
    Object.keys(CFG.palettes).forEach(function (k) {
      var P = CFG.palettes[k];
      pals.appendChild(h('button', { class: 'es-pal' + (d.palette === k ? ' is-on' : ''), type: 'button', role: 'radio', 'aria-checked': d.palette === k ? 'true' : 'false', onclick: function () { d.palette = k; d.userPalette = true; changed({ now: true, clearCache: true }); buildPanel(); } }, [
        h('i', { style: { background: 'linear-gradient(135deg,' + P.bg + ' 0 50%,' + P.paper + ' 50% 75%,' + P.accent + ' 75%)' } }), h('span', { text: P.label })
      ]));
    });
    R.panel.appendChild(section('Palette', [pals]));
    R.panel.appendChild(section(null, [h('button', { class: 'es-chip', type: 'button', onclick: function () {
      var nd = newDoc(d.layout); nd.photos = d.photos.slice(); nd.format = d.format; nd.sample = d.sample; S.doc = nd; S.sel = null; changed({ now: true, clearCache: true }); sizeCanvas(); buildPanel(); toast('Layout reset.');
    }, text: 'Reset this layout' })]));
    runThumbJobs();
  }

  /* ---------- Export ---------- */
  var X = {};
  function buildExport() {
    X.img = h('img', { class: 'es-export__img', alt: 'Your finished design' });
    X.status = h('p', { class: 'es-export__status', 'aria-live': 'polite' });
    X.type = h('div', { class: 'es-row' });
    X.size = h('div', { class: 'es-row' });
    X.credit = h('button', { class: 'es-chip', type: 'button', onclick: function () { S.doc.credit = !S.doc.credit; syncExportChips(); prepareExport(); } });
    X.dl = h('button', { class: 'es-btn es-btn--gold', type: 'button', onclick: downloadExport, text: 'Download' });
    X.share = h('button', { class: 'es-btn es-btn--ghost', type: 'button', onclick: shareExport, text: 'Share' });
    X.title = h('h2', { class: 'es-export__title', id: 'es-export-title' });
    X.el = h('div', { class: 'es-modal es-export', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'es-export-title', hidden: true }, [
      h('div', { class: 'es-export__inner' }, [
        h('div', { class: 'es-export__stage' }, [X.img, h('div', { class: 'es-export__dev', 'aria-hidden': 'true' })]),
        h('div', { class: 'es-export__side' }, [
          h('p', { class: 'es-kicker', text: 'Off the press' }),
          X.title,
          X.status,
          section('File', [X.type]), section('Resolution', [X.size]), section(null, [X.credit]),
          h('div', { class: 'es-export__actions' }, [X.dl, X.share]),
          h('p', { class: 'es-export__tag', text: 'Tag @eliowear.us' }),
          h('div', { class: 'es-row es-row--wrap' }, [
            h('button', { class: 'es-chip', type: 'button', onclick: closeExport, text: 'Keep editing' }),
            h('button', { class: 'es-chip', type: 'button', onclick: function () { closeExport(); leaveRoom(); }, text: 'Back to Studio' })
          ])
        ])
      ])
    ]);
    [['png', 'PNG'], ['jpg', 'JPG']].forEach(function (t) { X.type.appendChild(h('button', { class: 'es-chip', type: 'button', 'data-v': t[0], onclick: function () { S.exp.type = t[0]; syncExportChips(); prepareExport(); }, text: t[1] })); });
    [[1, 'Standard'], [2, 'High']].forEach(function (t) { X.size.appendChild(h('button', { class: 'es-chip', type: 'button', 'data-v': t[0], onclick: function () { S.exp.scale = t[0]; syncExportChips(); prepareExport(); }, text: t[1] })); });
    X.el.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeExport(); });
    app.appendChild(X.el);
  }
  function syncExportChips() {
    var F = CFG.formats[S.doc.format];
    Array.prototype.forEach.call(X.type.children, function (b) { b.classList.toggle('is-on', b.getAttribute('data-v') === S.exp.type); });
    Array.prototype.forEach.call(X.size.children, function (b) { var s = +b.getAttribute('data-v'); b.classList.toggle('is-on', s === S.exp.scale); b.textContent = (s === 1 ? 'Standard ' : 'High ') + F.w * s + ' × ' + F.h * s; });
    X.credit.textContent = S.doc.credit ? 'Studio credit on' : 'Studio credit off';
    X.credit.classList.toggle('is-on', !!S.doc.credit);
  }
  function openExport() {
    flushCommit();
    if (!X.el) buildExport();
    var L = CFG.layouts[S.doc.layout], nm = S.doc.elements.filter(function (e) { return e.role === 'Your name' && e.edited; })[0];
    X.title.textContent = nm ? nm.text + ', ready.' : ({ studio: 'Your cover is ready.', darkroom: 'Your print is dry.', press: 'Your page is printed.' })[S.room] || 'Ready.';
    X.share.textContent = IS_IOS ? 'Add to Photos' : 'Share';
    if (!navigator.share && !IS_IOS) X.share.textContent = 'Copy link';
    syncExportChips();
    X.el.hidden = false; X.el.classList.add('is-on');
    setTimeout(function () { X.dl.focus(); }, 60);
    track('project_completed', { mode: S.room, layout: S.doc.layout, format: S.doc.format, treatment: S.doc.treatment, photos: S.doc.photos.length });
    prepareExport();
  }
  function closeExport() { X.el.hidden = true; X.el.classList.remove('is-on'); }
  var exportSeq = 0;
  function prepareExport() {
    var seq = ++exportSeq;
    X.el.classList.add('is-developing'); X.status.textContent = 'Developing…';
    X.dl.disabled = true; X.share.disabled = true;
    S.exportBlob = null;
    setTimeout(function () {
      var F = CFG.formats[S.doc.format], s = S.exp.scale, W = F.w * s, H = F.h * s;
      var c = canvas(W, H), x = c.getContext('2d');
      try {
        Engine.render(x, S.doc, W, H, { src: 'full', nocache: true, wordmark: WORDMARK, credit: !!S.doc.credit });
      } catch (e) { X.status.textContent = 'That one did not develop. Try Standard resolution.'; free(c); return; }
      var type = S.exp.type === 'jpg' ? 'image/jpeg' : 'image/png';
      var fin = function (blob) {
        free(c);
        if (seq !== exportSeq) return;
        if (!blob) { X.status.textContent = 'That one did not develop. Try Standard resolution.'; return; }
        S.exportBlob = blob;
        if (S.exportUrl) URL.revokeObjectURL(S.exportUrl);
        S.exportUrl = URL.createObjectURL(blob); X.img.src = S.exportUrl;
        X.el.classList.remove('is-developing');
        X.status.textContent = W + ' × ' + H + '  ' + (S.exp.type.toUpperCase()) + '  ' + (blob.size / 1048576).toFixed(1) + ' MB';
        X.dl.disabled = false; X.share.disabled = false;
      };
      try { c.toBlob(fin, type, 0.93); } catch (e) { X.status.textContent = 'Add your own photo to save this one.'; free(c); }
    }, 60);
  }
  function fileName() { var L = CFG.layouts[S.doc.layout]; return 'elio-studio-' + L.label.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.' + S.exp.type; }
  function downloadExport() {
    if (!S.exportBlob) return;
    var a = h('a', { href: S.exportUrl, download: fileName() }); document.body.appendChild(a); a.click(); a.remove();
    track('file_exported', { mode: S.room, layout: S.doc.layout, format: S.doc.format, type: S.exp.type, scale: S.exp.scale, credit: !!S.doc.credit });
    toast('Downloaded.');
  }
  function shareExport() {
    if (!S.exportBlob) return;
    var file = null;
    try { file = new File([S.exportBlob], fileName(), { type: S.exportBlob.type }); } catch (e) {}
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      track('share_initiated', { method: 'file', mode: S.room, layout: S.doc.layout });
      navigator.share({ files: [file] }).then(function () { track('file_exported', { mode: S.room, layout: S.doc.layout, format: S.doc.format, type: S.exp.type, via: 'share' }); }).catch(function () {});
      return;
    }
    var url = location.origin + location.pathname;
    if (navigator.share) { track('share_initiated', { method: 'link', mode: S.room }); navigator.share({ title: 'ELIO Studio', url: url }).catch(function () {}); return; }
    track('share_initiated', { method: 'copy', mode: S.room });
    (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()).then(function () { toast('Studio link copied.'); }, function () { toast(url, 5000); });
  }

  /* =============================================================
     The Profile Set
     A six photo shot list. Each slot takes a photo the visitor has,
     or one taken now (alone with the timer, or by someone else with
     direction on screen). One treatment develops the whole set so it
     reads as one body of work. Nothing leaves the device.
     ============================================================= */
  var SET = { mode: 'self', slots: [], active: 0, treatment: 'clean', intensity: 0.85, started: false };
  var SX = {};
  var SHOTS = CFG.shots || [];

  function setDoc(i) {
    var sl = SET.slots[i] || {};
    return {
      layout: 'portrait', palette: 'black', treatment: SET.treatment, intensity: SET.intensity, adj: {},
      photos: sl.id ? [sl.id] : [], sample: null,
      slots: [Object.assign({ x: 0, y: 0, s: 1, r: 0, flip: false, t: 1 }, sl.t || {})],
      elements: [{ id: 'photos', k: 'photos' }], credit: false
    };
  }
  function filledCount() { return SET.slots.filter(function (s) { return s && s.id; }).length; }

  function openSet(mode) {
    if (mode) SET.mode = mode;
    if (!SX.el) buildSet();
    if (!SET.started) { SET.started = true; track('set_started', { path: SET.mode }); }
    transition('fade', '', function () {
      show('set');
      setActive(firstEmpty());
      sizeSet();
    });
  }
  function firstEmpty() { for (var i = 0; i < SHOTS.length; i++) if (!SET.slots[i] || !SET.slots[i].id) return i; return SET.active || 0; }
  function leaveSet() { show('lobby'); buildLobby(); }

  function buildSet() {
    SX.progress = h('span', { class: 'es-bar__sub es-set__progress' });
    SX.save = h('button', { class: 'es-btn es-btn--gold es-bar__finish', type: 'button', onclick: openSetExport, text: 'Develop set' });
    var bar = h('header', { class: 'es-bar es-set__bar' }, [
      h('button', { class: 'es-icon-btn', type: 'button', 'aria-label': 'Back', onclick: leaveSet, html: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' }),
      h('div', { class: 'es-bar__title' }, [h('span', { text: 'Your set' }), SX.progress]),
      h('div', { class: 'es-bar__tools' }, [SX.save])
    ]);
    SX.canvas = h('canvas', { class: 'es-canvas', role: 'img', 'aria-label': 'Selected photo, developed' });
    SX.empty = h('div', { class: 'es-set__empty' });
    SX.wrap = h('div', { class: 'es-canvas-wrap es-set__wrap' }, [SX.canvas, SX.empty]);
    SX.stage = h('div', { class: 'es-stage es-set__stage' }, [SX.wrap]);
    SX.strip = h('nav', { class: 'es-set__strip', 'aria-label': 'Shot list' });
    SHOTS.forEach(function (sh, i) {
      var c = canvas(96, 120);
      var b = h('button', { class: 'es-set__slot', type: 'button', 'data-i': i, onclick: function () { setActive(i); } }, [
        h('span', { class: 'es-set__thumb' }, [c]),
        h('span', { class: 'es-set__slotnum', text: '0' + (i + 1) }),
        h('span', { class: 'es-set__slotname', text: sh.short })
      ]);
      SX.strip.appendChild(b);
    });
    SX.panel = h('div', { class: 'es-panel es-set__panel' });
    SX.dock = h('aside', { class: 'es-dock es-set__dock' }, [SX.panel]);
    SX.file = h('input', { type: 'file', accept: 'image/*', multiple: true, class: 'es-sr', tabindex: '-1', 'aria-hidden': 'true' });
    SX.file.addEventListener('change', function () { fillFromFiles(SX.file.files, 'upload'); SX.file.value = ''; });
    SX.cap = h('input', { type: 'file', accept: 'image/*', capture: 'environment', class: 'es-sr', tabindex: '-1', 'aria-hidden': 'true' });
    SX.cap.addEventListener('change', function () { fillFromFiles(SX.cap.files, 'camera'); SX.cap.value = ''; });
    SX.el = views.set = h('section', { class: 'es-set', hidden: true }, [bar, SX.stage, SX.strip, SX.dock, SX.file, SX.cap]);
    app.appendChild(SX.el);
    bindSetGestures();
    if (window.ResizeObserver) new ResizeObserver(function () { if (S.view === 'set') sizeSet(); }).observe(SX.stage);
    else window.addEventListener('resize', function () { if (S.view === 'set') sizeSet(); });
  }

  function sizeSet() {
    var r = SX.stage.getBoundingClientRect(); if (!r.width || !r.height) return;
    var pad = r.width < 560 ? 12 : 30, ar = 4 / 5;
    var w = Math.min(r.width - pad * 2, (r.height - pad * 2) * ar), hh = w / ar;
    SX.wrap.style.width = Math.round(w) + 'px'; SX.wrap.style.height = Math.round(hh) + 'px';
    var dpr = Math.min(2, window.devicePixelRatio || 1), bw = Math.min(1400, Math.round(w * dpr));
    SX.canvas.width = bw; SX.canvas.height = Math.round(bw / ar);
    SX.cssW = w; SX.cssH = hh;
    drawSet();
  }
  var setQueued = false, setDraft = false;
  function drawSet(draft) {
    setDraft = setDraft || !!draft;
    if (setQueued) return; setQueued = true;
    requestAnimationFrame(function () {
      setQueued = false; var d = setDraft; setDraft = false;
      var i = SET.active, sl = SET.slots[i], x = SX.canvas.getContext('2d');
      if (sl && sl.id) Engine.render(x, setDoc(i), SX.canvas.width, SX.canvas.height, { src: 'proxy', draft: d });
      else { x.fillStyle = '#0d0c0b'; x.fillRect(0, 0, SX.canvas.width, SX.canvas.height); }
      SX.empty.hidden = !!(sl && sl.id);
      if (d) settleSet();
    });
  }
  var settleSet = debounce(function () { drawSet(false); }, 180);

  function drawThumb(i) {
    var b = SX.strip.children[i], c = b.querySelector('canvas'), sl = SET.slots[i];
    b.classList.toggle('is-done', !!(sl && sl.id));
    var x = c.getContext('2d');
    if (sl && sl.id) { c.width = 96; c.height = 120; Engine.render(x, setDoc(i), 96, 120, { src: 'proxy' }); }
    else { x.fillStyle = '#151311'; x.fillRect(0, 0, c.width, c.height); }
  }
  function refreshSetChrome() {
    var n = filledCount();
    SX.progress.textContent = n + ' of ' + SHOTS.length;
    SX.save.disabled = !n;
    Array.prototype.forEach.call(SX.strip.children, function (b, i) { b.classList.toggle('is-on', i === SET.active); b.setAttribute('aria-current', i === SET.active ? 'true' : 'false'); });
  }
  function setActive(i) {
    SET.active = clamp(i, 0, SHOTS.length - 1);
    refreshSetChrome(); buildSetPanel(); drawSet();
    SHOTS.forEach(function (s, k) { drawThumb(k); });
    var b = SX.strip.children[SET.active]; if (b && b.scrollIntoView) b.scrollIntoView({ block: 'nearest', inline: 'center' });
  }

  function direction(sh) { return sh[SET.mode] || sh.self; }

  /* Quality checks, computed on the device from the preview copy. */
  function quality(id) {
    var im = IMAGES[id]; if (!im) return [];
    if (im.q) return im.q;
    var sc = Math.min(1, 256 / Math.max(im.w, im.h)), w = Math.max(8, Math.round(im.w * sc)), hh = Math.max(8, Math.round(im.h * sc));
    var c = canvas(w, hh), x = c.getContext('2d'); x.drawImage(im.proxy, 0, 0, w, hh);
    var d = x.getImageData(0, 0, w, hh).data; free(c);
    var g = new Float32Array(w * hh), sum = 0;
    for (var i = 0, j = 0; i < d.length; i += 4, j++) { g[j] = (d[i] * 0.2126 + d[i + 1] * 0.7152 + d[i + 2] * 0.0722) / 255; sum += g[j]; }
    var mean = sum / g.length, lsum = 0, lsq = 0, n = 0;
    for (var yy = 1; yy < hh - 1; yy++) for (var xx = 1; xx < w - 1; xx++) {
      var k = yy * w + xx, l = g[k - 1] + g[k + 1] + g[k - w] + g[k + w] - 4 * g[k]; lsum += l; lsq += l * l; n++;
    }
    var lap = lsq / n - (lsum / n) * (lsum / n), out = [];
    if (mean < 0.16) out.push('Too dark. Move closer to a window or a lamp.');
    else if (mean > 0.84) out.push('Too bright. Step out of direct light.');
    if (lap < 0.0012) out.push('Soft focus. Hold still, or use the timer.');
    if (Math.min(im.w, im.h) < 800) out.push('Low resolution. It may look soft once posted.');
    im.q = out; im.qLap = lap; im.qMean = mean;
    return out;
  }

  function buildSetPanel() {
    var P = SX.panel; P.innerHTML = ''; P.scrollTop = 0;
    var i = SET.active, sh = SHOTS[i], sl = SET.slots[i];
    var modes = h('div', { class: 'es-row es-row--wrap es-set__modes', role: 'radiogroup', 'aria-label': 'How you are shooting' });
    [['self', 'On my own'], ['other', 'Someone’s shooting'], ['have', 'I have one']].forEach(function (m) {
      modes.appendChild(h('button', { class: 'es-chip' + (SET.mode === m[0] ? ' is-on' : ''), type: 'button', role: 'radio', 'aria-checked': SET.mode === m[0] ? 'true' : 'false', onclick: function () { SET.mode = m[0]; buildSetPanel(); renderEmpty(); }, text: m[1] }));
    });
    var choose = h('button', { class: 'es-btn ' + (SET.mode === 'have' ? 'es-btn--gold' : 'es-btn--ghost'), type: 'button', onclick: function () { SX.file.click(); }, text: SET.mode === 'have' ? 'Choose photo' : 'Use a photo' });
    var take = h('button', { class: 'es-btn ' + (SET.mode === 'have' ? 'es-btn--ghost' : 'es-btn--gold'), type: 'button', onclick: takeNow, text: 'Take it now' });
    var head = h('div', { class: 'es-group es-set__head' }, [
      h('p', { class: 'es-set__count', text: '0' + (i + 1) + ' of 0' + SHOTS.length }),
      h('h2', { class: 'es-set__title', text: sh.title }),
      h('p', { class: 'es-set__dir', text: direction(sh) }),
      modes,
      h('div', { class: 'es-row' }, SET.mode === 'have' ? [choose, take] : [take, choose])
    ]);
    if (sl && sl.id) {
      var q = quality(sl.id);
      head.appendChild(h('p', { class: 'es-check' + (q.length ? ' is-warn' : ''), text: q.length ? q.join(' ') : 'Good light, sharp enough.' }));
    }
    P.appendChild(head);
    if (sl && sl.id) {
      var t = sl.t = sl.t || { x: 0, y: 0, s: 1, r: 0, flip: false };
      SX.zoom = slider('Zoom', t.s, 1, 4, 0.01, function (v) { return v.toFixed(2) + '×'; }, function (v, live) { t.s = v; clampSetT(i); drawSet(live); if (!live) drawThumb(i); });
      P.appendChild(section('Framing', [
        h('p', { class: 'es-note', text: 'Drag to reframe. Pinch or scroll to zoom.' }), SX.zoom,
        h('div', { class: 'es-row es-row--wrap' }, [
          h('button', { class: 'es-chip', type: 'button', onclick: function () { t.r = ((Math.round((t.r || 0) / 90) * 90 + 90 + 180) % 360) - 180; clampSetT(i); drawSet(); drawThumb(i); }, text: 'Turn 90°' }),
          h('button', { class: 'es-chip', type: 'button', onclick: function () { t.flip = !t.flip; drawSet(); drawThumb(i); }, text: 'Mirror' }),
          h('button', { class: 'es-chip', type: 'button', onclick: function () { sl.t = { x: 0, y: 0, s: 1, r: 0, flip: false }; buildSetPanel(); drawSet(); drawThumb(i); }, text: 'Reset' }),
          h('button', { class: 'es-chip', type: 'button', onclick: function () { var id = sl.id; SET.slots[i] = null; if (!SET.slots.some(function (o) { return o && o.id === id; })) dropImage(id); refreshSetChrome(); buildSetPanel(); drawSet(); drawThumb(i); renderEmpty(); }, text: 'Remove' })
        ])
      ]));
    }
    var tr = h('div', { class: 'es-row es-row--wrap', role: 'radiogroup', 'aria-label': 'Develop' });
    (CFG.setTreatments || ['clean']).forEach(function (k) {
      tr.appendChild(h('button', { class: 'es-chip' + (SET.treatment === k ? ' is-on' : ''), type: 'button', role: 'radio', 'aria-checked': SET.treatment === k ? 'true' : 'false', onclick: function () { SET.treatment = k; buildSetPanel(); drawSet(); SHOTS.forEach(function (s, n) { drawThumb(n); }); }, text: CFG.treatments[k].label }));
    });
    var inten = slider('Intensity', SET.intensity, 0, 1, 0.01, pct, function (v, live) { SET.intensity = v; drawSet(live); if (!live) SHOTS.forEach(function (s, n) { drawThumb(n); }); });
    P.appendChild(section('Develop the whole set', [h('p', { class: 'es-note', text: 'One look across all six, so the set reads as one.' }), tr, inten]));
    renderEmpty();
  }
  function renderEmpty() {
    var i = SET.active, sh = SHOTS[i];
    SX.empty.innerHTML = '';
    SX.empty.appendChild(h('p', { class: 'es-set__emptynum', text: '0' + (i + 1) }));
    SX.empty.appendChild(h('p', { class: 'es-set__emptytitle', text: sh.title }));
    SX.empty.appendChild(h('p', { class: 'es-set__emptydir', text: direction(sh) }));
  }

  function takeNow() {
    var sh = SHOTS[SET.active];
    openCamera({
      hint: direction(sh), facing: SET.mode === 'other' ? 'environment' : 'user', timer: SET.mode === 'self' ? 10 : 0,
      onShot: function (id) { placeInSlot(SET.active, id, 'camera'); toast('Added to your set.'); },
      onFallback: function () { SX.cap.setAttribute('capture', SET.mode === 'other' ? 'environment' : 'user'); SX.cap.click(); }
    });
  }
  function placeInSlot(i, id, source) {
    var old = SET.slots[i] && SET.slots[i].id;
    SET.slots[i] = { id: id, t: { x: 0, y: 0, s: 1, r: 0, flip: false }, src: source };
    if (old && !SET.slots.some(function (o) { return o && o.id === old; })) dropImage(old);
    track('set_slot_filled', { slot: SHOTS[i].id, source: source, path: SET.mode, filled: filledCount() });
    if (filledCount() === SHOTS.length) track('set_complete', { path: SET.mode }, true);
    refreshSetChrome(); drawThumb(i);
    if (i === SET.active) { buildSetPanel(); drawSet(); }
  }
  function fillFromFiles(files, source) {
    files = Array.prototype.slice.call(files || []).filter(function (f) { return /^image\//.test(f.type) || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name); });
    if (!files.length) return;
    var targets = [SET.active];
    for (var k = 0; k < SHOTS.length && targets.length < files.length; k++) if (k !== SET.active && !(SET.slots[k] && SET.slots[k].id)) targets.push(k);
    files = files.slice(0, targets.length);
    SX.el.classList.add('is-busy'); toast('Adding…', 8000);
    var ok = 0, n = 0;
    var next = function () {
      if (n >= files.length) {
        SX.el.classList.remove('is-busy');
        if (ok) { toast(ok > 1 ? ok + ' photos added to your set.' : 'Added to your set.'); track('photo_imported', { source: source, count: ok, mode: 'set' }); }
        else toast('That file could not be opened here. Try a JPG or PNG.', 4000);
        return;
      }
      var slot = targets[n], f = files[n]; n++;
      decodeFile(f).then(function (id) { ok++; placeInSlot(slot, id, source); }).catch(function () {}).then(function () { setTimeout(next, 30); });
    };
    next();
  }

  function clampSetT(i) {
    var sl = SET.slots[i]; if (!sl || !sl.id) return; var im = IMAGES[sl.id]; if (!im) return;
    var f = { w: 4, h: 5 }, t = sl.t, base = Math.max(f.w / im.w, f.h / im.h) * (t.s || 1);
    var r = Math.abs(t.r || 0) % 180, rot = Math.min(r, 180 - r) * Math.PI / 180, cw = im.w * base, ch = im.h * base;
    if (rot > 0.004) { var k = Math.cos(rot); cw = cw * k - f.h * Math.sin(rot); ch = ch * k - f.w * Math.sin(rot); }
    var mx = Math.max(0, (cw - f.w) / 2 / f.w), my = Math.max(0, (ch - f.h) / 2 / f.h);
    t.x = clamp(t.x || 0, -mx, mx); t.y = clamp(t.y || 0, -my, my);
  }
  function bindSetGestures() {
    var ptrs = new Map(), g = null;
    function loc(e) { var r = SX.wrap.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }; }
    SX.wrap.addEventListener('pointerdown', function (e) {
      var sl = SET.slots[SET.active]; if (!sl || !sl.id) return;
      SX.wrap.setPointerCapture(e.pointerId); ptrs.set(e.pointerId, loc(e));
      var t = sl.t;
      if (ptrs.size === 1) g = { start: loc(e), ox: t.x, oy: t.y, t: t };
      else if (ptrs.size === 2 && g) { var p = Array.from(ptrs.values()); g.d = Math.hypot((p[1].x - p[0].x) * SX.cssW, (p[1].y - p[0].y) * SX.cssH); g.os = t.s; }
    });
    SX.wrap.addEventListener('pointermove', function (e) {
      if (!ptrs.has(e.pointerId) || !g) return; ptrs.set(e.pointerId, loc(e));
      if (ptrs.size === 1) { var p = loc(e); g.t.x = g.ox + (p.x - g.start.x); g.t.y = g.oy + (p.y - g.start.y); }
      else { var q = Array.from(ptrs.values()), d = Math.hypot((q[1].x - q[0].x) * SX.cssW, (q[1].y - q[0].y) * SX.cssH); g.t.s = clamp(g.os * d / (g.d || 1), 1, 4); }
      g.moved = true; clampSetT(SET.active); drawSet(true);
    });
    var end = function (e) { ptrs.delete(e.pointerId); if (!ptrs.size) { if (g && g.moved) { drawSet(false); drawThumb(SET.active); if (SX.zoom) { SX.zoom._inp.value = g.t.s; SX.zoom._out.textContent = SX.zoom._fmt(g.t.s); } } g = null; } };
    SX.wrap.addEventListener('pointerup', end); SX.wrap.addEventListener('pointercancel', end);
    SX.wrap.addEventListener('wheel', function (e) {
      var sl = SET.slots[SET.active]; if (!sl || !sl.id) return; e.preventDefault();
      sl.t.s = clamp(sl.t.s * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0018)), 1, 4); clampSetT(SET.active); drawSet(true);
      if (SX.zoom) { SX.zoom._inp.value = sl.t.s; SX.zoom._out.textContent = SX.zoom._fmt(sl.t.s); }
    }, { passive: false });
  }

  /* ---------- Saving the set ---------- */
  var SE = {};
  function openSetExport() {
    var filled = []; SET.slots.forEach(function (s, i) { if (s && s.id) filled.push(i); });
    if (!filled.length) return;
    if (!SE.el) {
      SE.grid = h('div', { class: 'es-setx__grid' });
      SE.status = h('p', { class: 'es-export__status', 'aria-live': 'polite' });
      SE.all = h('button', { class: 'es-btn es-btn--gold', type: 'button', onclick: saveAll });
      SE.cover = h('button', { class: 'es-btn es-btn--ghost', type: 'button', onclick: coverFromSet, text: 'Make a cover' });
      SE.el = h('div', { class: 'es-modal es-export es-setx', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'es-setx-title', hidden: true }, [
        h('div', { class: 'es-setx__inner' }, [
          h('p', { class: 'es-kicker', text: 'Developed' }),
          h('h2', { class: 'es-export__title', id: 'es-setx-title', text: 'Your set.' }),
          SE.status, SE.grid,
          h('div', { class: 'es-export__actions' }, [SE.all, SE.cover]),
          h('p', { class: 'es-note es-setx__tip', text: 'Lead with The Portrait. Keep the order.' }),
          h('div', { class: 'es-row es-row--wrap' }, [h('button', { class: 'es-chip', type: 'button', onclick: function () { SE.el.hidden = true; }, text: 'Keep editing' })])
        ])
      ]);
      SE.el.addEventListener('keydown', function (e) { if (e.key === 'Escape') SE.el.hidden = true; });
      app.appendChild(SE.el);
    }
    SE.el.hidden = false; SE.grid.innerHTML = ''; SE.files = [];
    SE.all.disabled = true; SE.all.textContent = IS_IOS ? 'Add all to Photos' : 'Download set';
    SE.status.textContent = 'Developing ' + filled.length + (filled.length > 1 ? ' photos…' : ' photo…');
    var cells = filled.map(function (i, n) {
      var img = h('img', { alt: SHOTS[i].title });
      var cell = h('button', { class: 'es-setx__cell', type: 'button', 'aria-label': 'Download ' + SHOTS[i].title, onclick: function () { saveOne(n); } }, [img, h('span', { text: (n + 1) + '  ' + SHOTS[i].title })]);
      SE.grid.appendChild(cell); return img;
    });
    var k = 0;
    var step = function () {
      if (k >= filled.length) {
        SE.status.textContent = filled.length + (filled.length > 1 ? ' photos' : ' photo') + ', 1080 × 1350' + (filled.length < SHOTS.length ? '. ' + (SHOTS.length - filled.length) + ' slots still open.' : '.');
        SE.all.disabled = false; track('project_completed', { mode: 'set', photos: filled.length, treatment: SET.treatment }); return;
      }
      var i = filled[k], c = canvas(1080, 1350);
      Engine.render(c.getContext('2d'), setDoc(i), 1080, 1350, { src: 'full', nocache: true });
      c.toBlob(function (blob) {
        free(c);
        var name = 'elio-set-' + (k + 1) + '-' + SHOTS[i].id + '.jpg';
        SE.files.push({ name: name, blob: blob });
        cells[k].src = URL.createObjectURL(blob);
        k++; setTimeout(step, 20);
      }, 'image/jpeg', 0.92);
    };
    setTimeout(step, 60);
  }
  function saveOne(n) {
    var f = SE.files[n]; if (!f) return;
    var file = null; try { file = new File([f.blob], f.name, { type: 'image/jpeg' }); } catch (e) {}
    if (IS_IOS && file && navigator.canShare && navigator.canShare({ files: [file] })) { navigator.share({ files: [file] }).catch(function () {}); return; }
    var url = URL.createObjectURL(f.blob), a = h('a', { href: url, download: f.name }); document.body.appendChild(a); a.click(); a.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
  }
  function saveAll() {
    if (!SE.files || !SE.files.length) return;
    var files = [];
    try { files = SE.files.map(function (f) { return new File([f.blob], f.name, { type: 'image/jpeg' }); }); } catch (e) {}
    if (files.length && navigator.canShare && navigator.canShare({ files: files }) && (IS_IOS || !/Mac|Win/.test(navigator.platform))) {
      track('share_initiated', { method: 'files', mode: 'set', count: files.length });
      navigator.share({ files: files }).then(function () { track('file_exported', { mode: 'set', count: files.length, via: 'share' }); }).catch(function () {});
      return;
    }
    Promise.all(SE.files.map(function (f) { return f.blob.arrayBuffer(); })).then(function (bufs) {
      var zip = makeZip(SE.files.map(function (f, i) { return { name: f.name, data: new Uint8Array(bufs[i]) }; }));
      var url = URL.createObjectURL(zip), a = h('a', { href: url, download: 'elio-set.zip' }); document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 6000);
      track('file_exported', { mode: 'set', count: SE.files.length, via: 'zip' }); toast('Downloaded.');
    });
  }
  var CRC = null;
  function crc32(d) {
    if (!CRC) { CRC = new Uint32Array(256); for (var n = 0; n < 256; n++) { var c = n; for (var k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1; CRC[n] = c >>> 0; } }
    var x = 0xFFFFFFFF; for (var i = 0; i < d.length; i++) x = CRC[(x ^ d[i]) & 255] ^ (x >>> 8); return (x ^ 0xFFFFFFFF) >>> 0;
  }
  function makeZip(files) {
    var parts = [], central = [], off = 0, enc = new TextEncoder();
    files.forEach(function (f) {
      var name = enc.encode(f.name), crc = crc32(f.data), sz = f.data.length;
      var lh = new DataView(new ArrayBuffer(30));
      lh.setUint32(0, 0x04034b50, true); lh.setUint16(4, 20, true); lh.setUint16(8, 0, true); lh.setUint32(14, crc, true);
      lh.setUint32(18, sz, true); lh.setUint32(22, sz, true); lh.setUint16(26, name.length, true);
      parts.push(lh.buffer, name, f.data);
      var ch = new DataView(new ArrayBuffer(46));
      ch.setUint32(0, 0x02014b50, true); ch.setUint16(4, 20, true); ch.setUint16(6, 20, true); ch.setUint32(16, crc, true);
      ch.setUint32(20, sz, true); ch.setUint32(24, sz, true); ch.setUint16(28, name.length, true); ch.setUint32(42, off, true);
      central.push(ch.buffer, name);
      off += 30 + name.length + sz;
    });
    var csize = central.reduce(function (a, b) { return a + (b.byteLength || b.length); }, 0);
    var end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true); end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
    end.setUint32(12, csize, true); end.setUint32(16, off, true);
    return new Blob(parts.concat(central, [end.buffer]), { type: 'application/zip' });
  }
  function coverFromSet() {
    var ids = SET.slots.filter(function (s) { return s && s.id; }).map(function (s) { return s.id; });
    if (!ids.length) return;
    SE.el.hidden = true;
    var d = newDoc('issue'); d.photos = ids.slice(); d.treatment = SET.treatment === 'clean' ? 'flash' : SET.treatment;
    startRoom('studio', d, 'Your set is loaded. Pick the frame for the cover.');
  }

  /* =============================================================
     Boot
     ============================================================= */
  function boot() {
    root.classList.add('is-ready');
    show('lobby'); buildLobby();
    track('studio_opened', { returning: visits.n > 0 });
    if (visits.n > 0) track('return_visit', { visits: visits.n + 1, days_since: visits.t ? Math.round((Date.now() - visits.t) / 864e5) : null });
    store('elio_studio_visits', { n: visits.n + 1, t: Date.now() });
    try { sessionStorage.setItem('elio_studio_session', '1'); } catch (e) {}
    loadFonts().then(function () { clearCache(); requestRender(); if (lobbyBuilt) refreshLobbyArt(); refreshThumbs(); });
    window.addEventListener('pagehide', function () { stopStream(); });
    if (/[?&]room=(studio|darkroom|press|archive)/.test(location.search)) enterRoom(RegExp.$1);
    else if (/[?&]set=(self|other|have)/.test(location.search)) openSet(RegExp.$1);
    window.ElioStudio = { state: S, engine: Engine, images: IMAGES, config: CFG, enterRoom: enterRoom, importFiles: function (f) { importFiles(f, 'upload'); } };
  }
  boot();
})();
